import { z } from 'zod';
import { sortTimelineCaptions } from './captionOrder';
import {
  CAPTION_ALIGNS,
  CAPTION_BACKGROUNDS,
  CAPTION_DEFAULTS,
  CAPTION_EDGE_POSITIONS,
  CAPTION_OUTLINE_JOINS,
  CAPTION_POSITIONS,
  CAPTION_RATIOS,
  opacityToAlphaHex,
  type CaptionOutlineJoin,
} from './captionCatalog';
import {
  CAPTION_ANIMATIONS_IN,
  CAPTION_ANIMATIONS_OUT,
  CAPTION_ANIMATION_SEC_MAX,
  CAPTION_ANIMATION_SEC_MIN,
  normalizeCaptionAnimation,
} from './captionAnimation';
import {
  CAPTION_EMPHASIS_SIZE_SCALE_MAX,
  CAPTION_EMPHASIS_SIZE_SCALE_MIN,
  normalizeCaptionEmphasisStyle,
  normalizeEmphasisRanges,
  parseEmphasisMarkup,
  resolveCaptionEmphasisStyle,
  type CaptionEmphasisRange,
} from './captionEmphasis';
import {
  CAPTION_WORD_HIGHLIGHT_LEAD_SEC_MAX,
  CAPTION_WORD_HIGHLIGHT_MODES,
  normalizeCaptionWordHighlight,
  normalizeWordTimings,
  type CaptionWordTimings,
} from './captionWordHighlight';
import {
  CAPTION_ROTATION_MAX_DEG,
  CAPTION_ROTATION_MIN_DEG,
  normalizeCaptionRotationValue,
} from './captionRotation';
import {
  MAX_AUDIO_FADE_SEC,
  applyExtensionConflicts,
  extensionAttributeKeys,
  normalizeCaptionLineHeightValue,
  normalizeCaptionOffsetPercentValue,
} from './extensionAttributes';
import { LOOK_PRESETS, type LookPreset } from './lookCatalog';
import { CropSchema } from './crop';
import { PlacementSchema } from './placement';
import { FreezeSchema, clampFreezeToRange, type ResolvedFreeze } from './freeze';
import { MotionSchema } from './motion';
import { EffectArraySchema } from './effects';
import { OverlayArraySchema } from './overlay';
import { LineageSchema, inferTimelineLineage } from './lineage';
import { TimeAnchorFieldSchema } from './timeAnchorSchema';
import { migrateLegacyAnchors, recomputeAnchoredTiming } from './timeAnchor';
import {
  audioRegionsEqual,
  normalizeAudioRegions,
  type AudioGainSpan,
  type AudioRegion,
} from '../audio/audioRegions';
import { CAPTION_BUBBLE_BACKGROUND, CaptionBubbleSchema } from './captionBubble';
import { CAPTION_GRADIENT_DIRECTIONS, normalizeCaptionGradient } from './captionGradient';
import {
  SpeedRampSchema,
  rampDisplayDurationSec,
  rampSourceSecAt,
  resolvedSpeedRamp,
  type ResolvedSpeedRamp,
} from './speedRamp';

// クロップ / リフレーム (§4.1-J) の正本は schema/crop.ts。既存の import 元を変えずに
// 済むよう、ここから再 export する (motion / overlay と同じ作法)。
export { CropSchema } from './crop';
export type { Crop, CropFit } from './crop';

// サイズと位置 (縮小 + 配置) の正本は schema/placement.ts (crop と同じ作法で再 export)。
export { PlacementSchema } from './placement';
export type { Placement } from './placement';

// 映像エフェクト (§4.1-S) の正本は schema/effects.ts。既存の import 元を変えずに済むよう、
// ここから再 export する (crop / overlay と同じ作法)。
export { EffectArraySchema, EffectSchema } from './effects';
export type { Effect, EffectKind } from './effects';

// フリーズフレーム / 速度ランプ (§4.1-L) の正本は schema/freeze.ts / schema/speedRamp.ts。
// 既存の import 元を変えずに済むよう、ここから再 export する。
export { FreezeSchema } from './freeze';
export type { Freeze, ResolvedFreeze } from './freeze';
export { SpeedRampSchema } from './speedRamp';
export type { SpeedRamp, SpeedRampEasing, ResolvedSpeedRamp } from './speedRamp';

// オーバーレイ (画像 / ぼかし / モザイクの矩形) の正本は schema/overlay.ts。
// 既存の import 元 (schema/project) を変えずに済むよう、ここから再 export する。
export { OverlaySchema, OverlayArraySchema } from './overlay';
export type { Overlay, OverlayKind, OverlayRect, OverlayFit } from './overlay';
import { TransitionSchema, transitionOverlapSec } from './transition';
import {
  effectiveEndSec,
  migrateProjectBgmTrack,
  minAudioCueSec,
  type AudioTrackKind,
} from '../audio/audioCue';

// テロップ design の選択肢 (列挙・既定値・比率・刻み・AI 上限・丸め) の正本は schema/captionCatalog.ts。
// 既存の import 元を変えずに済むよう、ここから再 export する。
export {
  CAPTION_AI_LIMITS,
  CAPTION_ALIGNS,
  CAPTION_BACKGROUNDS,
  CAPTION_DEFAULTS,
  CAPTION_EDGE_POSITIONS,
  CAPTION_OUTLINE_JOINS,
  CAPTION_POSITIONS,
  CAPTION_RATIOS,
  CAPTION_STEPS,
  opacityToAlphaHex,
  roundToStep,
  type CaptionOutlineJoin,
} from './captionCatalog';

/**
 * "編集プロジェクト" 中間表現のスキーマ定義。
 *
 * Ported from video-editor repo (src/schema/project.ts)。
 * 変更点(image-generator 統合に伴う素材参照の適応):
 * - SourceClip / AudioCue (BGM トラックのキュー) に materialId(temporary_materials / materials の ID)と
 *   isTemporary(個人フォルダ/共有フォルダの別)を追加
 * - src / narrationSrc / bgmTrack[].src は「ロード時に署名付き URL を充填する導出値」に変更
 *   (optional 化)。保存時は stripResolvedUrls() で必ず除去し、DB には永続化しない
 * - ナレーション音声も narrationMaterialId / narrationIsTemporary で参照する
 *
 * UI (@remotion/player) と将来のサーバーレンダラーが共有する正式な中間表現であり、
 * AI (Claude) の出力・ユーザーの手動修正の両方が最終的にこの形へ収束する。
 */

export const CaptionStyleSchema = z.enum(['default', 'emphasis']);
export type CaptionStyle = z.infer<typeof CaptionStyleSchema>;

// ── テロップの位置補正・行間 (拡張属性。§4.2-C) ───────────────────
// 列挙の position は UI 用にそのまま残し、そこからの**補正**と行間を足して任意位置を表せるようにする
// (docs/features/video-editor-capability-gaps-plan.md §0-1: AI の値空間 ⊇ UI の値空間)。
// schema/extensionAttributes.ts が扱い (競合: position を変えたら補正を捨てる) を宣言する。

/** offsetXPercent / offsetYPercent の許容範囲 (出力の幅 / 高さに対する %)。 */
export const CAPTION_OFFSET_LIMIT_PERCENT = 50;
/** 上下帯 (letterbox) の中央の動画領域の比率 (幅 / 高さ) の範囲と既定 (1:1)。 */
export const CAPTION_LETTERBOX_RATIO_MIN = 0.25;
export const CAPTION_LETTERBOX_RATIO_MAX = 4;
export const CAPTION_LETTERBOX_RATIO_DEFAULT = 1;
/** 上下帯の基準線を求めるときに出力サイズが分からない場合の既定 (9:16)。 */
export const CAPTION_LETTERBOX_FALLBACK_OUTPUT = { width: 1080, height: 1920 } as const;
/** 字間 (letterSpacingEm) の許容範囲と刻み (em)。負で詰める。 */
export const CAPTION_LETTER_SPACING_EM_MIN = -0.3;
export const CAPTION_LETTER_SPACING_EM_MAX = 1;
export const CAPTION_LETTER_SPACING_EM_STEP = 0.01;

/** design の字間 (em)。不正値・丸めて 0 は 0。保存形 (0.01 刻み) と同じ値を返す。 */
export function resolveCaptionLetterSpacingEm(design: { letterSpacingEm?: number | null } | null | undefined): number {
  return normalizeCaptionLetterSpacingEmValue(design?.letterSpacingEm) ?? 0;
}

/** 字間の保存形: 範囲にクランプして 0.01 刻みに丸め、0 なら undefined (キーごと落とす)。 */
export function normalizeCaptionLetterSpacingEmValue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const clamped = Math.min(CAPTION_LETTER_SPACING_EM_MAX, Math.max(CAPTION_LETTER_SPACING_EM_MIN, value));
  const rounded = Math.round(clamped / CAPTION_LETTER_SPACING_EM_STEP) * CAPTION_LETTER_SPACING_EM_STEP;
  const fixed = Math.round(rounded * 100) / 100;
  return fixed === 0 ? undefined : fixed;
}

/** lineHeight (行間倍率) の許容範囲。 */
export const CAPTION_LINE_HEIGHT_MIN = 0.8;
export const CAPTION_LINE_HEIGHT_MAX = 2.5;
/** lineHeight 未指定時の行間: 通常は 1.3、箱 (box) は上下の余白ぶん広げて 1.7。 */
export const CAPTION_LINE_HEIGHT_DEFAULT = 1.3;
export const CAPTION_LINE_HEIGHT_BOX = 1.7;

// AI が決めるテロップの見た目。fontFamily は fonts/captionFonts.ts の許可リスト
// のフォント名(旧データの "gothic"|"mincho"|"rounded" もレンダラー側で解決)。
// 縁取り・影・左右揃えは後から追加したため optional(未指定はレンダラー既定)。
export const CaptionDesignSchema = z.object({
  fontFamily: z.string().min(1),
  // 数値ウェイト (100〜900。フォントが提供するものに丸めて描画) または旧データの "normal"/"bold"。
  fontWeight: z.union([z.enum(['normal', 'bold']), z.number().int().min(100).max(900)]),
  fontSizePx: z.number().positive(),
  color: z.string(),
  hollow: z.boolean().optional(),
  // 斜体。CSS の font-style: italic (日本語の Web フォントには本物のイタリック体が無いので、ブラウザーの
  // 合成の斜体 = 字形ごとに約 14° 傾ける)。テロップ全体を回す rotationDeg とは別で、併用できる。
  // 箱 (box) / 帯 (band) の背景は傾かない。省略 = 斜体にしない。
  italic: z.boolean().optional(),
  // 文字のグラデーション (テレビ風の 2 色塗り)。1 色目は color、2 色目と向きをここに持つ。
  // 省略 = 単色。意味・CSS は schema/captionGradient.ts。中抜き (hollow) では描画しない (値は保持)。
  gradient: z
    .object({
      color: z.string(),
      direction: z.enum(CAPTION_GRADIENT_DIRECTIONS).optional(),
      split: z.boolean().optional(),
    })
    .optional(),
  // 字間 (em。負で詰める・正で空ける)。省略 = 0。単語ハイライトの字間 (CAPTION_WORD_HIGHLIGHT_LETTER_SPACING_EM)
  // とは加算。範囲・刻みは CAPTION_LETTER_SPACING_EM_MIN / MAX / STEP。
  letterSpacingEm: z.number().min(CAPTION_LETTER_SPACING_EM_MIN).max(CAPTION_LETTER_SPACING_EM_MAX).optional(),
  // "outline" は旧データ互換(縁取りは outlineWidthPx に移行)。
  // band = 画面幅いっぱいの帯、box = 文字にフィットした箱 (1 行ごと)、none = なし。
  // "outline" は旧データ互換 (縁取りは outlineWidthPx に移行)。
  // bubble = 全行を囲む吹き出し (schema/captionBubble.ts。AI の選択肢 CAPTION_BACKGROUNDS には入れない)。
  background: z.enum([...CAPTION_BACKGROUNDS, CAPTION_BUBBLE_BACKGROUND, 'outline'] as const),
  // 縦位置は 9 段階 (upper/lower は縦型動画のセーフゾーン用に、端と中央の中間。topUpper/bottomLower/
  // upperCenter/lowerCenter はさらにその間) + 画面端に密着させる
  // header / footer (セーフゾーンを無視。全幅の帯を敷いた見出し・字幕用途)。
  position: z.enum(CAPTION_POSITIONS),
  align: z.enum(CAPTION_ALIGNS).optional(),
  writingMode: z.enum(['horizontal-tb', 'vertical-rl']).optional(),
  // background="band" のときの帯の色 (CSS カラー。#RRGGBBAA で透過も表せる)。
  // 省略時はレンダラー既定の rgba(0,0,0,0.65)。
  backgroundColor: z.string().optional(),
  // background="box" の角丸半径 (px)。0 = 角。省略時はフォントサイズの 15% (bubble は 35%)。
  backgroundRadiusPx: z.number().nonnegative().optional(),
  // background="bubble" の見出し・アイコン・しっぽ (schema/captionBubble.ts)。bubble 以外では使わない (値は保持する)。
  bubble: CaptionBubbleSchema.optional(),
  // background="band" の帯の上下余白 (px)。文字の行 (フォントサイズ × 1.3) の上下にこれだけ帯を
  // 広げる。0 = 行にぴったり。省略時はフォントサイズの 50% (resolveBandPaddingPx)。
  backgroundPaddingPx: z.number().nonnegative().optional(),
  outlineWidthPx: z.number().nonnegative().optional(),
  outlineColor: z.string().optional(),
  // 縁取り・外縁の角の形: round (既定) = 丸、miter = 角 (remotion/captionOutline の resolveOutlineRender 参照)。
  // 位置は常に外側 (文字の縁から外に太さぶん)。
  outlineJoin: z.enum(CAPTION_OUTLINE_JOINS).optional(),
  // 2重縁取りの外側の縁(内側の縁の外にさらに足される太さ)。日本の YouTube テロップ風。
  outline2WidthPx: z.number().nonnegative().optional(),
  outline2Color: z.string().optional(),
  // ドロップシャドウ (resolveCaptionShadow で解決)。
  // - shadowOpacity: 0〜1。UI ではこれが > 0 なら影あり。未指定で offset/blur が入っている旧データは不透明度 1 扱い
  // - 角度は 135° (右下) 固定、距離は offset 未指定ならフォントサイズの 10% (CAPTION_SHADOW_DISTANCE_RATIO)
  // - shadowSizePx: 影の広がり (Premiere の Size)。文字の輪郭を外側に太らせてから落とす
  // - shadowBlurPx: ぼかし (Premiere の Blur)
  // - shadowOffsetXPx / YPx: 明示オフセット (px、右・下が正)。未指定 = 自動 (角度 135°・距離はフォント 10%)。
  //   編集 UI の「位置 X / Y」と AI 提案の両方から書く
  shadowColor: z.string().optional(),
  shadowOpacity: z.number().min(0).max(1).optional(),
  shadowSizePx: z.number().nonnegative().optional(),
  shadowBlurPx: z.number().nonnegative().optional(),
  shadowOffsetXPx: z.number().optional(),
  shadowOffsetYPx: z.number().optional(),
  // ── 拡張属性 (UI に対応するコントロールが無い / 少ない AI 向けの値空間。§0-1) ──
  // 位置の微調整 (§4.2-C): position の列挙で決まる基準線からの補正。出力の幅 / 高さに対する %
  // (正 = 右 / 下)。header / footer (画面端に密着する配置) には効かない (正規化で落とす)。
  // position の列挙を変えたら捨てる (競合宣言は schema/extensionAttributes.ts)。
  offsetXPercent: z.number().min(-CAPTION_OFFSET_LIMIT_PERCENT).max(CAPTION_OFFSET_LIMIT_PERCENT).optional(),
  offsetYPercent: z.number().min(-CAPTION_OFFSET_LIMIT_PERCENT).max(CAPTION_OFFSET_LIMIT_PERCENT).optional(),
  // 行間 (倍率)。省略 = 箱 1.7 / それ以外 1.3 (resolveCaptionLineHeight)。position とは独立。
  lineHeight: z.number().min(CAPTION_LINE_HEIGHT_MIN).max(CAPTION_LINE_HEIGHT_MAX).optional(),
  // 回転 (度・正 = 時計回り)。省略 = 傾けない。意味・数式は schema/captionRotation.ts。
  // **帯 (band) と header / footer では効かない** (正規化で落とす)。position を変えても保持する。
  rotationDeg: z.number().min(CAPTION_ROTATION_MIN_DEG).max(CAPTION_ROTATION_MAX_DEG).optional(),
  // 上下帯 (position = letterboxTop / letterboxBottom) で想定する**中央の動画領域の縦横比** (幅 / 高さ)。
  // 省略 = 1 (1:1)。画面の中央にこの比率の領域 (幅いっぱい) を置いたときの上下の余白 (帯) の中心に
  // テロップの中心を合わせる (captionAnchorRatio)。上下帯以外の位置では使わない (値は保持する)。
  letterboxRatio: z.number().min(CAPTION_LETTERBOX_RATIO_MIN).max(CAPTION_LETTERBOX_RATIO_MAX).optional(),
  // 入場 / 退場アニメーション (§4.2-B1)。省略 = なし。意味・数式は schema/captionAnimation.ts。
  animationIn: z.enum(CAPTION_ANIMATIONS_IN).optional(),
  animationOut: z.enum(CAPTION_ANIMATIONS_OUT).optional(),
  animationInSec: z.number().min(CAPTION_ANIMATION_SEC_MIN).max(CAPTION_ANIMATION_SEC_MAX).optional(),
  animationOutSec: z.number().min(CAPTION_ANIMATION_SEC_MIN).max(CAPTION_ANIMATION_SEC_MAX).optional(),
  // テロップ内の部分強調のスタイル (§4.2-I)。**どこを**強調するかは Caption.emphasisRanges 側。
  // 省略 = 既定 (黄色系 × 1.2 倍 + 太字)。意味・数式は schema/captionEmphasis.ts。
  emphasis: z
    .object({
      color: z.string().optional(),
      sizeScale: z.number().min(CAPTION_EMPHASIS_SIZE_SCALE_MIN).max(CAPTION_EMPHASIS_SIZE_SCALE_MAX).optional(),
      bold: z.boolean().optional(),
    })
    .optional(),
  // 単語ハイライト (カラオケ字幕。§4.2-B2) の見た目。**キーがあること自体が「使う」宣言**で、
  // 中身が空 `{}` でも有効 (省略 = 使わない)。**どこを・いつ**光らせるかは Caption.wordTimings 側で、
  // そちらはコードの同期工程が作る。意味・数式は schema/captionWordHighlight.ts。
  wordHighlight: z
    .object({
      color: z.string().optional(),
      mode: z.enum(CAPTION_WORD_HIGHLIGHT_MODES).optional(),
      leadSec: z.number().min(0).max(CAPTION_WORD_HIGHLIGHT_LEAD_SEC_MAX).optional(),
    })
    .optional(),
});
export type CaptionDesign = z.infer<typeof CaptionDesignSchema>;

/**
 * design 未指定のテロップ (シナリオから取り込んだ直後・手動追加) の既定デザイン。
 * テロップ編集 UI (CaptionEditor) が design の無いテロップをこの値で表示するので、
 * レンダラー (EditVideo) も同じ値で描画し、編集画面とプレビュー/書き出しを一致させる
 * (以前はレンダラーだけ灰色の帯付きで描いていたため「編集では背景なしなのに再生すると帯が出る」
 * 状態だった)。video-editor リポジトリの src/schema/project.ts にも同じ定義がある。
 */
/** 縁取りの既定色 (濃いグレー)。 */
export const DEFAULT_CAPTION_OUTLINE_COLOR: string = CAPTION_DEFAULTS.outlineColor;
/** 2 重縁取りの外縁の既定色 (白)。YouTube テロップ風の「白文字 + 色の内縁 + 白の外縁」を想定。 */
export const DEFAULT_CAPTION_OUTLINE2_COLOR: string = CAPTION_DEFAULTS.outline2Color;
/**
 * 帯 / 箱 (background="band" / "box") の既定色 (#RRGGBBAA)。カタログの bandRgb + bandOpacity の合成で、
 * レンダラー既定と一致する (#000000A6 = 黒 65%)。
 */
export const DEFAULT_CAPTION_BAND_COLOR = `${CAPTION_DEFAULTS.bandRgb}${opacityToAlphaHex(CAPTION_DEFAULTS.bandOpacity)}`;

/** 縦型・正方形 (isPortraitOutput) の既定フォントサイズ。横型の 46px より一回り大きくする (1080 幅で約 5.5%)。 */
export const DEFAULT_CAPTION_FONT_SIZE_PORTRAIT_PX: number = CAPTION_DEFAULTS.fontSizePortraitPx;

/**
 * テロップ既定値 (文字サイズ・位置・縁取り・話者色・SNS テンプレートの逃がし) を縦型にするか。
 * 正方形も縦型扱いにする (上下に静止画を足して Instagram / TikTok の縦型フィードで流すことが多いため)。
 * video-editor リポジトリの src/schema/project.ts と backend caption_catalog.is_portrait_output にも同じ定義がある。
 */
export function isPortraitOutput(output: { width: number; height: number }): boolean {
  return output.width > 0 && output.height > 0 && output.height >= output.width;
}

/**
 * 出力サイズに応じた既定デザイン。縦型・正方形 (isPortraitOutput) は「やや下」(中央と下の中間、左右中央) と
 * 大きめのフォントサイズを、横型は「下」を既定にする。design 未指定のテロップの表示
 * (編集 UI・レンダラー) とテロップ追加時の初期値の両方でこれを使う。
 */
export function defaultCaptionDesignFor(output: { width: number; height: number }): CaptionDesign {
  const portrait = isPortraitOutput(output);
  return {
    ...DEFAULT_CAPTION_DESIGN,
    fontSizePx: portrait ? DEFAULT_CAPTION_FONT_SIZE_PORTRAIT_PX : DEFAULT_CAPTION_DESIGN.fontSizePx,
    position: portrait ? CAPTION_DEFAULTS.positionPortrait : CAPTION_DEFAULTS.positionLandscape,
  };
}

export const DEFAULT_CAPTION_DESIGN: CaptionDesign = {
  fontFamily: CAPTION_DEFAULTS.fontFamily,
  fontWeight: CAPTION_DEFAULTS.fontWeight,
  fontSizePx: CAPTION_DEFAULTS.fontSizePx,
  color: CAPTION_DEFAULTS.color,
  background: CAPTION_DEFAULTS.background,
  position: CAPTION_DEFAULTS.positionLandscape,
  // outlineWidthPx は未指定 = フォントサイズに応じた自動の太さ (defaultOutlineWidthPx)。
  // 0 を入れると縁取りなし。
  outlineColor: DEFAULT_CAPTION_OUTLINE_COLOR,
};

/**
 * 縦位置 → 画面高さに対する基準線の位置 (%)。テロップブロックの同じ割合の点をその線に合わせる
 * (0% なら上端、50% なら中心、100% なら下端が揃う) ので、9 段階を同じ式で扱える。
 * レンダラー (remotion/EditVideo) と Premiere 書き出しのテロップ画像化 (export/captionRaster) で共有する。
 */
export const CAPTION_POSITION_PERCENT: Record<CaptionDesign['position'], number> = {
  top: 0,
  topUpper: 12.5,
  upper: 25,
  upperCenter: 37.5,
  center: 50,
  lowerCenter: 62.5,
  lower: 75,
  bottomLower: 87.5,
  bottom: 100,
  header: 0,
  footer: 100,
  // 上下帯は**テロップの中心**を帯の中心に合わせる (基準線は captionAnchorRatio が出力比率から計算する)。
  // 下付き / 上付き (Inner) は動画領域の側の縁に寄せる: 上の帯はテロップの下端、下の帯は上端を合わせる
  letterboxTop: 50,
  letterboxBottom: 50,
  letterboxTopInner: 100,
  letterboxBottomInner: 0,
};

/**
 * 上下帯 (letterbox) の配置。縦型の画面の中央に動画領域 (幅いっぱい・縦横比 letterboxRatio) を
 * 想定し、その上下の余白 (帯) の中央にテロップを置く。左右は中央固定で、位置補正は効かない。
 * 動画そのものは変えない (テロップの位置だけの設定)。
 */
export const CAPTION_LETTERBOX_POSITIONS = [
  'letterboxTop', 'letterboxBottom', 'letterboxTopInner', 'letterboxBottomInner',
] as const;

/**
 * 上下帯の下付き / 上付き (letterboxTopInner / letterboxBottomInner) で、テロップと動画領域の縁の間に
 * 空ける間隔 (画面高さに対する割合)。
 */
export const CAPTION_LETTERBOX_INNER_GAP_RATIO = 0.015;

/** 上の帯 (letterboxTop / letterboxTopInner) か。 */
function isTopLetterboxPosition(position: CaptionDesign['position']): boolean {
  const resolved = resolveCaptionPosition(position);
  return resolved === 'letterboxTop' || resolved === 'letterboxTopInner';
}

export function isLetterboxCaptionPosition(position: CaptionDesign['position'] | string | null | undefined): boolean {
  return (CAPTION_LETTERBOX_POSITIONS as readonly string[]).includes(resolveCaptionPosition(position));
}

/** 上下帯の中央の動画領域の比率 (幅 / 高さ)。省略・範囲外は 1 (1:1)。 */
export function resolveLetterboxRatio(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value)
    && value >= CAPTION_LETTERBOX_RATIO_MIN && value <= CAPTION_LETTERBOX_RATIO_MAX
    ? value
    : CAPTION_LETTERBOX_RATIO_DEFAULT;
}

/**
 * 上下帯の 1 本の高さ (画面高さに対する 0〜0.5)。中央の動画領域 (幅 = 画面幅、高さ = 幅 / ratio) が
 * 画面より高ければ帯は無い (0)。
 */
export function letterboxBandRatio(output: { width: number; height: number }, ratio: number | null | undefined): number {
  if (!(output.width > 0 && output.height > 0)) return 0;
  const inner = output.width / resolveLetterboxRatio(ratio) / output.height;
  return Math.max(0, (1 - inner) / 2);
}

/**
 * テロップのセーフエリア (画面の中央 90%)。縦位置の列挙 (captionAnchorRatio) と縦書きの高さ fit が使う。
 * 左右は captionSideMarginRatio / captionSafeWidthRatio (9:16 だけ広い) を使うこと。
 */
export const CAPTION_SAFE_AREA_RATIO = 0.9;

/**
 * 9:16 (縦型ショート動画) の左右セーフマージン (片側・画面幅比)。TikTok In-Feed 広告の公式セーフゾーン
 * (720×1280 で左右 80px の縁 + 右側のボタン列) に合わせて 1080 幅で 120px (約 11%)。
 * 上下の位置計算 (captionAnchorRatio) は CAPTION_SAFE_AREA_RATIO (各辺 5%) のまま。媒体別セーフエリアの
 * 導入時に上下左右へ一般化する (video-distribution-media-spec-plan.md §3.5)。
 * video-editor の schema/project.ts と backend caption_extensions.py にも同じ定義がある。
 */
export const CAPTION_SIDE_MARGIN_RATIO_9_16 = 120 / 1080;

/** 出力が 9:16 (1080×1920 / 720×1280 など) か。高さ ÷ 幅が 16/9 から ±0.03 以内。 */
export function isNineSixteenOutput(output: { width: number; height: number }): boolean {
  return output.width > 0 && output.height > 0 && Math.abs(output.height / output.width - 16 / 9) < 0.03;
}

/**
 * テロップの左右セーフマージン (片側・画面幅比)。9:16 は CAPTION_SIDE_MARGIN_RATIO_9_16、それ以外は
 * 従来どおり (1 - CAPTION_SAFE_AREA_RATIO) / 2 = 5%。左右揃えの基準点・帯の左右 padding・横幅の fit
 * (captionSafeWidthRatio) はすべてこれを使う。
 */
export function captionSideMarginRatio(output: { width: number; height: number }): number {
  return isNineSixteenOutput(output) ? CAPTION_SIDE_MARGIN_RATIO_9_16 : (1 - CAPTION_SAFE_AREA_RATIO) / 2;
}

/** 左右セーフマージン (片側・%)。浮動小数の誤差を丸めて DOM と 3 実装の値を揃える。 */
export function captionSideMarginPercent(output: { width: number; height: number }): number {
  return Math.round(captionSideMarginRatio(output) * 100 * 1e6) / 1e6;
}

/** テロップ 1 行が収まるべき横幅 (画面幅比) = 1 - 左右マージン × 2。 */
export function captionSafeWidthRatio(output: { width: number; height: number }): number {
  return 1 - 2 * captionSideMarginRatio(output);
}

/**
 * セーフゾーンを無視して画面の端に密着させる配置 (header = 上端、footer = 下端)。
 * 帯 (band) を画面幅いっぱいに敷いた見出し・字幕用途で、上下 5% の余白を取らない
 * (左右の文字の余白と横幅の fit は通常の配置と同じ)。
 */
export function isEdgeCaptionPosition(position: CaptionDesign['position']): boolean {
  return (CAPTION_EDGE_POSITIONS as readonly string[]).includes(resolveCaptionPosition(position));
}

/**
 * 縦位置の列挙を解決する。**欠落・不正値は既定の配置に倒す** (以前は
 * `CAPTION_POSITION_PERCENT[undefined]` が NaN になり、補正・基準線・ブロック高さが軒並み NaN に
 * なって「位置補正がキーごと消える」挙動になっていた)。
 *
 * 既定はカタログの横型既定 (`positionLandscape` = bottom)。カタログの値が列挙に無い場合だけ
 * `bottom` に落とす。**backend の caption_extensions は同じ既定 (100% = 下端) を使う** ので、
 * position 欠落のデータでも 3 実装が同じ値になる。
 */
export function resolveCaptionPosition(
  position: CaptionDesign['position'] | string | null | undefined,
): CaptionDesign['position'] {
  if (typeof position === 'string' && position in CAPTION_POSITION_PERCENT) {
    return position as CaptionDesign['position'];
  }
  const fallback = CAPTION_DEFAULTS.positionLandscape;
  return (fallback in CAPTION_POSITION_PERCENT ? fallback : 'bottom') as CaptionDesign['position'];
}

/** 縦位置 → ブロックのどの割合の点を基準線に合わせるか (%)。欠落・不正値は既定の配置に倒す。 */
export function captionPositionPercent(
  position: CaptionDesign['position'] | string | null | undefined,
): number {
  return CAPTION_POSITION_PERCENT[resolveCaptionPosition(position)];
}

/**
 * 縦位置 → 基準線の画面高さに対する比率 (0〜1)。通常の段はセーフエリアの内側
 * (5% + P × 90%)、header / footer は画面端そのもの (0 / 1)、上下帯は出力と letterboxRatio から
 * 求めた帯の中心 (output 省略時は 9:16 とみなす)。テロップブロックの
 * CAPTION_POSITION_PERCENT の割合の点をこの線に合わせる。
 * レンダラー (remotion/EditVideo) と Premiere / Final Cut 書き出し (captionBlockGeometry) で共有する。
 */
export function captionAnchorRatio(
  position: CaptionDesign['position'],
  output: { width: number; height: number } = CAPTION_LETTERBOX_FALLBACK_OUTPUT,
  letterboxRatio?: number,
): number {
  if (isLetterboxCaptionPosition(position)) {
    // 中央: 帯の中心 (上の帯は 帯/2、下の帯は 1 - 帯/2)。帯が無い比率では画面の端 (0 / 1) になる。
    // 下付き / 上付き: 動画領域の縁から間隔ぶん帯の側 (上の帯は 帯 - 間隔、下の帯は 1 - 帯 + 間隔)
    const band = letterboxBandRatio(output, letterboxRatio);
    const inner = resolveCaptionPosition(position).endsWith('Inner');
    const top = inner ? Math.max(0, band - CAPTION_LETTERBOX_INNER_GAP_RATIO) : band / 2;
    return isTopLetterboxPosition(position) ? top : 1 - top;
  }
  const percent = captionPositionPercent(position) / 100;
  if (isEdgeCaptionPosition(position)) return percent;
  return (1 - CAPTION_SAFE_AREA_RATIO) / 2 + percent * CAPTION_SAFE_AREA_RATIO;
}

// ── 位置補正・行間 (拡張属性 §4.2-C) の解決 ───────────────────────
// レンダラー (remotion/EditVideo・VerticalCaption)・書き出しの幾何 (export/premiereXml の
// captionBlockGeometry)・合成フレーム (backend/services/video_edit/contact_sheet.py) が共有する。
// backend/services/video_edit/caption_extensions.py に同じ規則の正規化がある (3 実装で同値)。

/**
 * 補正の上限を決めるための**想定ブロック高さ** (画面高さに対する割合)。
 *
 * テロップブロックの実際の高さは描画時 (フォント・行数・帯の余白) にしか分からないので、
 * 保存時のクランプ (resolveCaptionOffsetPercent) ではこの想定値で「ブロックがおおよそ画面に
 * 収まる」範囲に補正を絞る。2 行のテロップ (1920 高さ・60px・帯の余白込みで約 0.15) を目安にした
 * 近似で、実際のブロックがこれより高いときは描画側の clampCaptionBlockTop が最終的に押し戻す。
 */
export const CAPTION_NOMINAL_BLOCK_HEIGHT_RATIO = 0.15;

/**
 * 位置補正 (offsetYPercent) の許容範囲 (%)。基準線に**ブロックの percent の点**が吊られるので、
 * 上に percent × H、下に (1 - percent) × H だけブロックが広がる (H = 想定ブロック高さ)。
 * その両端が画面内に収まる範囲を、列挙位置の基準線からの相対値 (%) で返す。
 */
export function captionOffsetYLimitPercent(position: CaptionDesign['position']): { min: number; max: number } {
  const base = captionAnchorRatio(position);
  const percent = captionPositionPercent(position) / 100;
  const nominal = CAPTION_NOMINAL_BLOCK_HEIGHT_RATIO;
  // 上端が 0 以上 / 下端が 1 以下になる基準線の範囲 (想定ブロック高さでの近似)
  const minAnchor = percent * nominal;
  const maxAnchor = 1 - (1 - percent) * nominal;
  // 保存する補正と同じ 0.1 刻み (half-up) に丸めて、3 実装で同じ境界値にする
  // (浮動小数の誤差でクランプ結果が 1e-16 ずれるのを防ぐ。切り上げ / 切り捨てにすると
  //  ちょうど境界の値 (top の -5% など) が 0.1 ぶん内側に寄ってしまうので使わない)
  const round1 = (value: number) => Math.floor(value * 10 + 0.5) / 10;
  return {
    min: round1(Math.max(-CAPTION_OFFSET_LIMIT_PERCENT, (Math.min(minAnchor, maxAnchor) - base) * 100)),
    max: round1(Math.min(CAPTION_OFFSET_LIMIT_PERCENT, (Math.max(minAnchor, maxAnchor) - base) * 100)),
  };
}

/**
 * design が持つ位置補正 (%)。header / footer (画面端に密着する配置) と上下帯では効かせない。
 * 縦は captionOffsetYLimitPercent の範囲 (テロップがおおよそ画面に収まる範囲) に、
 * 横は ±CAPTION_OFFSET_LIMIT_PERCENT にクランプする。
 */
export function resolveCaptionOffsetPercent(
  design: Pick<CaptionDesign, 'position' | 'offsetXPercent' | 'offsetYPercent'>,
): { x: number; y: number } {
  if (isEdgeCaptionPosition(design.position) || isLetterboxCaptionPosition(design.position)) return { x: 0, y: 0 };
  const num = (value: number | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const limitY = captionOffsetYLimitPercent(design.position);
  return {
    x: Math.min(CAPTION_OFFSET_LIMIT_PERCENT, Math.max(-CAPTION_OFFSET_LIMIT_PERCENT, num(design.offsetXPercent))),
    y: Math.min(limitY.max, Math.max(limitY.min, num(design.offsetYPercent))),
  };
}

/**
 * 補正込みの基準線 (画面高さに対する 0〜1)。補正は resolveCaptionOffsetPercent で
 * 「想定ブロック高さでおおよそ画面に収まる」範囲に絞ってあるので、ここでは 0〜1 に収めるだけ。
 * **実際のブロック高さでの最終的なはみ出し防止は描画側の clampCaptionBlockTop** が行う
 * (ブロック高さは描画時にしか分からないため)。
 */
export function resolveCaptionAnchorRatio(
  design: Pick<CaptionDesign, 'position' | 'offsetXPercent' | 'offsetYPercent' | 'letterboxRatio'>,
  output?: { width: number; height: number },
): number {
  const base = captionAnchorRatio(design.position, output, design.letterboxRatio);
  const offset = resolveCaptionOffsetPercent(design).y / 100;
  // 浮動小数の誤差 (0.05 + 0.75 × 0.9 = 0.7250000000000001) を丸めて、3 実装と DOM の値を揃える
  return Math.round(Math.min(1, Math.max(0, base + offset)) * 1e6) / 1e6;
}

/**
 * テロップブロックの上端を画面内に収める (§4.2-C)。基準線 1 点のクランプでは、ブロックが基準線から
 * 上下に広がるぶん (center + 大きな下方向の補正など) 画面外にはみ出すため、**実際のブロック高さ**が
 * 分かる描画側でもう一度押し戻す。ブロックが画面より高いときは上端を 0 に寄せる (上を優先して見せる)。
 *
 * レンダラー (DesignedCaption)・書き出しの幾何 (captionBlockGeometry)・合成フレーム
 * (contact_sheet._draw_caption_block) で同じ式を使う。
 */
export function clampCaptionBlockTop(top: number, blockHeight: number, outputHeight: number): number {
  return Math.max(0, Math.min(top, outputHeight - blockHeight));
}

/**
 * 行間の下限 (floor) を決める design のサブセット。行間は倍率なので、下限は
 * **フォントサイズに対する比**でしか決まらない (縁取りの px / フォントサイズ)。
 */
export type CaptionLineHeightDesign = Pick<CaptionDesign, 'background' | 'lineHeight'> &
  Partial<Pick<CaptionDesign, 'fontSizePx' | 'outlineWidthPx' | 'outline2WidthPx' | 'emphasis'>>;

/**
 * 行の**実体 (インク) の高さ** (em)。和文フォントの CJK グリフは em ボックスいっぱい
 * (ideographic 0.88 / −0.12) に描かれるので 1.0 とする。
 *
 * 実測 (PIL の getbbox。backend/tests/test_video_edit_caption_extensions.py の
 * test_caption_ink_height_em_matches_measured_fonts が実行環境のフォントで検証する):
 * ヒラギノ角ゴ W3 / W6 = 0.965 / 0.970、ヒラギノ明朝 = 0.940 / 0.965、ヒラギノ丸ゴ W4 = 0.970。
 * Noto Sans JP / Noto Serif JP (Cloud Run の Noto CJK) も同じ em ボックス設計。
 * 家族ごとの差は 0.03 em 未満なので 1 定数で扱う (差が問題になったらカタログのフォント表へ)。
 */
export const CAPTION_INK_HEIGHT_EM = 1.0;

/**
 * 箱 (background: 'box') の上下 padding (em)。箱は inline の span に
 * box-decoration-break: clone で描いており、EditVideo.tsx / captionRaster.ts の box padding と同じ値
 * (3 実装 + BE の contact_sheet.py で同値のはず。プレビュー描画側は直接このリテラルを持つ)。
 */
export const CAPTION_BOX_PADDING_EM = 0.12;

/**
 * 斜体 (design.italic) の傾き (tan)。日本語の Web フォントには本物のイタリック体が無く、
 * フォントも normal だけを読み込む (captionFontLoader) ので、斜体はブラウザー (Skia) の合成の斜体
 * = 字形を横に 0.25 (約 14°) 傾けたものになる。プレビューも MP4 のレンダラーも Chromium なので
 * 同じ見た目になる。字送りは変わらず、字形の上側が右へはみ出すので、フィット縮小ではこの em を
 * 行の幅に足して測る (EditVideo / VerticalCaption / captionRaster / BE の contact_sheet で同値)。
 */
export const CAPTION_ITALIC_SKEW = 0.25;

/** テロップの font-style (斜体なら 'italic'、それ以外は undefined = 既定の normal)。 */
export function captionFontStyle(design: Pick<CaptionDesign, 'italic'>): 'italic' | undefined {
  return design.italic ? 'italic' : undefined;
}

/**
 * 箱の縦の大きさの見積もり (em) = フォントの content area (ascent + descent)。行送り
 * (lineHeight × fontSize) とは独立に、箱はこの高さ + 上下 padding ぶんを占める。
 * Noto Sans JP (Cloud Run の CJK フォント) の fontBoundingBox は 1.167 (ascent) + 0.283
 * (descent) = 1.45 em。切り上げて 1.46 とする。
 *
 * 日本語の Google Fonts は概ね Noto と同じ em 設計 (ascent/descent がこの値付近) なので、
 * これより content area が小さいフォントでは箱の間に隙間ができるだけ (重ならない)。逆に
 * これより大きいフォントは、行間の既定 (箱 1.7) でも従来からわずかに重なりうるが、許容する。
 */
export const CAPTION_BOX_CONTENT_AREA_EM = 1.46;

/**
 * 行と行の間に最低限あける空き (em)。下限 (floor) が通常のテロップの見た目を変えないよう、
 * 「既定 1.3 + 自動の縁取り」で floor が 1.3 を超えない値にしてある
 * (1.0 + 2 × 0.1 + 0.06 = 1.26 < 1.3)。floor が既定を超えるのは、縁取りを太くしたときと
 * 部分強調で文字を拡大したときだけ。
 */
export const CAPTION_LINE_GAP_EM = 0.06;

/**
 * 縁取りの太さが未指定 (自動) のときに下限の計算で使う比 (フォントサイズ比)。
 * 自動の太さは出力の向きで変わる (横型 5% / 縦型 10%) が、行間は出力サイズを見ずに決めたいので
 * **太い方 (縦型)** を使う (安全側)。
 */
export const CAPTION_OUTLINE_AUTO_RATIO_FOR_LINE_HEIGHT: number = CAPTION_RATIOS.outlineWidthPortrait;

/**
 * 行間の**下限** (倍率。§4.2-C)。行送りがこれを下回ると、隣の行の文字 (+ 縁取り) が重なる。
 *
 *     floor = インク高 × 強調の拡大率 + 2 × (縁取り + 外縁) / フォントサイズ + 行の空き
 *
 * - 縁取り (outlineWidthPx / outline2WidthPx) は文字の輪郭の**外**に太さぶん広がるので、上下で
 *   2 倍効く。太さが未指定 (自動) のときは CAPTION_OUTLINE_AUTO_RATIO_FOR_LINE_HEIGHT で見積もる
 * - 部分強調 (§4.2-I) は**強調範囲を持つテロップ**でだけ文字が sizeScale 倍になるので、
 *   `hasEmphasisRanges` が true のときだけ掛ける (単語ハイライトの color-bold は太さだけなので掛けない)
 * - **影 (shadow) は数えない**: 影の層は文字の層の**下**に敷くので、隣の行の文字に重なっても
 *   文字自体は隠れない (縁取りは文字と同じ層の外側に出るので数える)
 *
 * **箱 (background: 'box') は別枠でもう一つ下限がある**: 箱の縦の大きさ
 * (CAPTION_BOX_CONTENT_AREA_EM + 上下 padding CAPTION_BOX_PADDING_EM × 2) は行送りとは独立に
 * 決まるので、これを下回ると箱同士が重なる。上のインク・縁取りの下限とは**大きい方**を採る
 * (強調で文字が拡大すれば箱もそのぶん大きくなる想定で sizeScale を掛ける)。
 *
 * 3 実装 (FE / レンダラー / backend の caption_extensions) で同値。**0.01 刻みに切り上げて**
 * 返すので、保存形 (normalizeCaptionExtensions が丸める刻み) と同じ値になる。
 */
export function captionLineHeightFloor(
  design: CaptionLineHeightDesign,
  hasEmphasisRanges = false,
): number {
  const fontSizePx =
    typeof design.fontSizePx === 'number' && Number.isFinite(design.fontSizePx) && design.fontSizePx > 0
      ? design.fontSizePx
      : 0;
  // 縁取りの太さ (px) をフォントサイズ比に直す。未指定 (自動) とフォントサイズ不明は比で見積もる
  const outlineEm = (value: number | undefined, autoRatio: number): number => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return autoRatio;
    return fontSizePx > 0 ? value / fontSizePx : autoRatio;
  };
  const sizeScale = hasEmphasisRanges ? resolveCaptionEmphasisStyle(design).sizeScale : 1;
  const inkEm = CAPTION_INK_HEIGHT_EM * sizeScale;
  const floor =
    inkEm +
    2 * (outlineEm(design.outlineWidthPx, CAPTION_OUTLINE_AUTO_RATIO_FOR_LINE_HEIGHT) + outlineEm(design.outline2WidthPx, 0)) +
    CAPTION_LINE_GAP_EM;
  const boxFloor =
    design.background === 'box'
      ? CAPTION_BOX_CONTENT_AREA_EM * sizeScale + 2 * CAPTION_BOX_PADDING_EM
      : 0;
  // 0.01 刻みに**切り上げ** (丸めでは下限を下回りうる)。浮動小数の誤差 (1.0 + 0.2 + 0.06 =
  // 1.2600000000000002) でちょうど刻みの値が 1 段上がらないよう、微小量を引いてから切り上げる
  const rounded = Math.ceil(Math.max(floor, boxFloor) * 100 - 1e-9) / 100;
  return Math.min(CAPTION_LINE_HEIGHT_MAX, Math.max(CAPTION_LINE_HEIGHT_MIN, rounded));
}

/**
 * 行間 (倍率)。指定が無ければ箱 (box) は CAPTION_LINE_HEIGHT_BOX、それ以外は既定値。
 * どちらの場合も**下限 (captionLineHeightFloor) を下回らない** (縁取り・部分強調で行が重なるため)。
 *
 * `hasEmphasisRanges` は「このテロップが部分強調の範囲を持つか」(Caption.emphasisRanges)。
 * design だけでは分からないので描画側が渡す (省略 = 強調なし)。
 */
export function resolveCaptionLineHeight(
  design: CaptionLineHeightDesign,
  hasEmphasisRanges = false,
): number {
  const floor = captionLineHeightFloor(design, hasEmphasisRanges);
  if (typeof design.lineHeight === 'number' && Number.isFinite(design.lineHeight)) {
    return Math.max(
      floor,
      Math.min(CAPTION_LINE_HEIGHT_MAX, Math.max(CAPTION_LINE_HEIGHT_MIN, design.lineHeight)),
    );
  }
  return Math.max(floor, design.background === 'box' ? CAPTION_LINE_HEIGHT_BOX : CAPTION_LINE_HEIGHT_DEFAULT);
}

/**
 * 行間が既定値と違うか (書き出しの注記・要約・design 指針の同梱で使う)。
 *
 * **実効値と既定値 (箱 1.7 / それ以外 1.3) の比較 1 本**で判定する。指定の有無では見ない:
 * 縁取りが太いテロップは `lineHeight` を書いていなくても下限 (captionLineHeightFloor) で
 * 既定より広がるので、キーの有無で早期に false を返すと「実際は 1.77 で描いているのに
 * 要約にも XML の注記にも出ない」状態になる (§4.2-C)。
 * 自動の縁取り (= 既存のほぼ全部) では下限が既定を超えないので、既定のテロップは false のまま。
 */
export function hasCustomCaptionLineHeight(
  design: CaptionLineHeightDesign,
): boolean {
  const fallback = design.background === 'box' ? CAPTION_LINE_HEIGHT_BOX : CAPTION_LINE_HEIGHT_DEFAULT;
  return Math.abs(resolveCaptionLineHeight(design) - fallback) > 1e-6;
}

/**
 * design が持つ回転角 (度・正 = 時計回り)。
 *
 * **帯 (band) と画面端の配置 (header / footer) では効かせない**: 帯は画面幅いっぱいに敷く層なので
 * 回すと片側に隙間ができ、header / footer は画面端に密着させる配置なので傾けると必ず端から浮く。
 * 横位置の補正 (offsetXPercent) と同じ扱いで、正規化 (normalizeCaptionExtensions) がキーごと落とす。
 * 数値の範囲・刻み (±30・0.5) は captionRotation.ts が持つ (3 実装で同値)。
 *
 * **保存形と同じ 0.5 刻みに丸めて返す**: 描画 (この関数) と要約・書き出し (保存値) が同じ角度を
 * 使うようにするため (丸めずに返すと、保存されない 8.2° でプレビューだけ傾く)。
 */
export function resolveCaptionRotationDeg(
  design: Pick<CaptionDesign, 'position' | 'background' | 'rotationDeg'>,
): number {
  if (isEdgeCaptionPosition(design.position) || design.background === 'band') return 0;
  return normalizeCaptionRotationValue(design.rotationDeg) ?? 0;
}

/** 回転が効いているか (要約・UI・書き出しで使う)。保存形 (0.5 刻み) で判定する。 */
export function hasCaptionRotation(
  design: Pick<CaptionDesign, 'position' | 'background' | 'rotationDeg'>,
): boolean {
  return normalizeCaptionRotationValue(resolveCaptionRotationDeg(design)) !== undefined;
}

/** 位置補正が設定されているか (要約・書き出しの注記で使う)。 */
export function hasCaptionOffset(
  design: Pick<CaptionDesign, 'position' | 'offsetXPercent' | 'offsetYPercent'>,
): boolean {
  // 保存形 (0.1 刻みに丸めた実効値) で判定する。normalizeCaptionExtensions と同じ基準にして、
  // 「要約・書き出しの注記には出るのに保存されていない」状態を作らない
  const offset = resolveCaptionOffsetPercent(design);
  return (
    normalizeCaptionOffsetPercentValue(offset.x) !== undefined ||
    normalizeCaptionOffsetPercentValue(offset.y) !== undefined
  );
}

/**
 * 拡張属性 (位置補正・行間・アニメーション) を保存形へ正規化する。**保存値 = 実効値**にするため、
 * 効かない指定 (header / footer の補正・丸めて 0 になる補正・種類の無い秒数) はキーごと落とす。
 *
 * 値ごとの規則 (範囲・刻み・0 の扱い) は拡張属性の宣言 (schema/extensionAttributes.ts) の
 * normalize に委譲し、ここは design 全体を見ないと決まらない部分 (header / footer で補正を落とす・
 * 行間の既定値が背景の形で変わる) だけを持つ。backend の
 * caption_extensions.normalize_caption_extensions と同じ規則 (3 実装で同値)。
 */
export function normalizeCaptionExtensions(
  design: Pick<
    CaptionDesign,
    | 'position'
    | 'background'
    | 'offsetXPercent'
    | 'offsetYPercent'
    | 'lineHeight'
    | 'rotationDeg'
    | 'animationIn'
    | 'animationOut'
    | 'animationInSec'
    | 'animationOutSec'
    | 'emphasis'
    | 'hollow'
    | 'italic'
    | 'gradient'
    | 'letterSpacingEm'
    | 'wordHighlight'
  > &
    // 行間の下限 (縁取り・強調ぶん) の計算に使う (captionLineHeightFloor)。
    // 呼び出し側は**正規化後の px** (クランプ済みのフォントサイズ・縁取り) を渡すこと
    Partial<Pick<CaptionDesign, 'fontSizePx' | 'outlineWidthPx' | 'outline2WidthPx'>>,
): Partial<CaptionDesign> {
  const result: Partial<CaptionDesign> = {};
  const offset = resolveCaptionOffsetPercent(design);
  const x = normalizeCaptionOffsetPercentValue(offset.x);
  const y = normalizeCaptionOffsetPercentValue(offset.y);
  if (x !== undefined) result.offsetXPercent = x;
  if (y !== undefined) result.offsetYPercent = y;
  if (typeof design.lineHeight === 'number' && Number.isFinite(design.lineHeight)) {
    // 下限 (縁取りぶん) を下回る指定は**切り上げて保存**する (保存値 = 実効値)。
    // 部分強調の拡大ぶんは design だけでは分からない (テロップの emphasisRanges 次第) ので
    // 保存値には含めず、描画時に resolveCaptionLineHeight がもう一段引き上げる
    result.lineHeight = normalizeCaptionLineHeightValue(resolveCaptionLineHeight(design));
  }
  // 回転 (テロップ回転)。帯 (band) / header / footer では効かないので、resolveCaptionRotationDeg が
  // 0 を返し、丸めて 0 の値と一緒にキーごと落ちる (保存値 = 実効値)
  const rotation = normalizeCaptionRotationValue(resolveCaptionRotationDeg(design));
  if (rotation !== undefined) result.rotationDeg = rotation;
  // 部分強調のスタイル (§4.2-I)。既定と同じ値・不正値は落ちる (保存値 = 実効値)
  if (typeof design.hollow === 'boolean') result.hollow = design.hollow;
  if (typeof design.italic === 'boolean') result.italic = design.italic;
  // 文字のグラデーション。2 色目が #RRGGBB でなければキーごと落ちる (保存値 = 実効値)
  const gradient = normalizeCaptionGradient(design.gradient);
  if (gradient) result.gradient = gradient;
  // 字間 (em)。丸めて 0 ならキーごと落ちる (保存値 = 実効値)
  const letterSpacing = normalizeCaptionLetterSpacingEmValue(design.letterSpacingEm);
  if (letterSpacing !== undefined) result.letterSpacingEm = letterSpacing;
  const emphasis = normalizeCaptionEmphasisStyle(design.emphasis);
  if (emphasis) result.emphasis = emphasis;
  // 単語ハイライト (§4.2-B2)。**キーの存在が「使う」宣言**なので、既定値だけなら空 `{}` を残す
  const wordHighlight = normalizeCaptionWordHighlight(design.wordHighlight);
  if (wordHighlight) result.wordHighlight = wordHighlight;
  return { ...result, ...normalizeCaptionAnimation(design) };
}

/**
 * UI のフォーム・提案の引き継ぎが design に部分更新を当てるときの共通処理 (§0-3 / §5-0)。
 * 1. UI が知らない属性を落とさない (spread)
 * 2. 競合する属性 (position) を別の値に変えたら、競合する拡張属性 (位置補正) を捨てる
 * 3. 拡張属性を保存形へ正規化する (保存値 = 実効値。効かない指定はキーごと落ちる)
 *
 * **FE で design に patch を当てる経路はすべてこれを通す** (CaptionEditor のツールバー編集)。
 * design を丸ごと差し替える経路 (テロップ追加時の既定 design・「他のテロップにも適用」・提案の
 * 引き継ぎ) は patch ではないのでここは通らず、それぞれの場所で正規化・位置補正の除去を行う。
 * backend の同じ経路は normalize_caption_design + drop_conflicting_caption_extensions。
 */
export function applyCaptionDesignPatch(
  current: CaptionDesign,
  patch: Partial<CaptionDesign>,
): CaptionDesign {
  const merged = applyExtensionConflicts(
    'captionDesign',
    current as unknown as Record<string, unknown>,
    patch as unknown as Record<string, unknown>,
  ) as unknown as CaptionDesign;
  return normalizeCaptionDesignExtensions(merged);
}

/**
 * 位置補正 (offsetX / offsetY) を外した design。テロップ固有のずらし幅なので、
 * 別のテロップへ design をコピーする経路 (「他のテロップにも適用」・提案の代表 design) では落とす。
 */
export function stripCaptionOffsets(design: CaptionDesign): CaptionDesign {
  if (design.offsetXPercent === undefined && design.offsetYPercent === undefined) return design;
  const next = { ...design };
  delete next.offsetXPercent;
  delete next.offsetYPercent;
  return next;
}

/**
 * **別のテロップへ design を配る経路**で落とす、テロップ固有の設定を外した design。
 * 対象は位置補正 (§4.2-C)・単語ハイライト (§4.2-B2)・回転 (テロップ回転)。
 *
 * 回転は「バッジ風の 1 枚だけ傾ける」使い方を想定した設定なので、配ると全テロップが傾いて
 * 読みづらくなる。位置補正と同じく、配る経路 (「他のテロップにも適用」・提案の代表 design・
 * backend の `find_representative_design`) で落とす
 * (backend の `apply_caption_design` (AI の一括適用) では従来どおり配れる)。
 *
 * 単語ハイライトは「発話と対応づけられたテロップ」でだけ意味を持つ設定で、配ると
 * 対応づけの無いテロップに「有効だが光らない」状態 (要約の『未同期』・lint の指摘) を量産する。
 * 位置補正と同じく、配る経路 (「他のテロップにも適用」・提案の代表 design・
 * backend の `find_representative_design`) で落とす。
 * backend の `apply_caption_design` (AI の一括適用) は**wordHighlight のみ**を落とす
 * (位置補正は「まとめて少し上げる」に使えるため従来どおり配る)。
 *
 * **戻り値は入力と同一参照のことがある** (落とすキーが 1 つも無いとき)。呼び出し側は戻り値を
 * その場で書き換えないこと。
 */
export function stripSharedCaptionDesign(design: CaptionDesign): CaptionDesign {
  const stripped = stripCaptionOffsets(design);
  if (stripped.wordHighlight === undefined && stripped.rotationDeg === undefined) return stripped;
  const next = { ...stripped };
  delete next.wordHighlight;
  delete next.rotationDeg;
  return next;
}

/** design の拡張属性だけを保存形に揃える (他のキーはそのまま)。 */
export function normalizeCaptionDesignExtensions(design: CaptionDesign): CaptionDesign {
  const next = { ...design } as unknown as Record<string, unknown>;
  for (const key of extensionAttributeKeys('captionDesign')) delete next[key];
  return { ...(next as unknown as CaptionDesign), ...normalizeCaptionExtensions(design) };
}

/**
 * テロップ本体の拡張属性 (部分強調の範囲) を保存形に揃える (§4.2-I)。
 * text の長さでクランプし、重なり・空区間をまとめ、1 つも残らなければキーごと落とす
 * (保存値 = 実効値)。**入力は変更せず、変化が無ければ同じ参照を返す**。
 */
export function normalizeCaptionEmphasisRanges<T extends { text: string; emphasisRanges?: CaptionEmphasisRange[] }>(
  caption: T,
): T {
  if (caption.emphasisRanges === undefined) return caption;
  const normalized = normalizeEmphasisRanges(caption.emphasisRanges, Array.from(caption.text ?? '').length);
  const same =
    normalized !== undefined &&
    normalized.length === caption.emphasisRanges.length &&
    normalized.every((r, i) => r.start === caption.emphasisRanges![i].start && r.end === caption.emphasisRanges![i].end);
  if (same) return caption;
  const next = { ...caption };
  if (normalized) next.emphasisRanges = normalized;
  else delete next.emphasisRanges;
  return next;
}

/**
 * UI の文言エディタが記法付き文字列 (`**強調**`) を保存形 (プレーン text + emphasisRanges) に戻す
 * (§4.2-I)。**文言を編集しても強調が残る**のはこの往復のおかげで、記法を書かずに文言を変えれば
 * 強調はその場で消える (text に依存する属性の破棄 = §0-3)。
 */
export function applyCaptionTextMarkup<
  T extends { text: string; emphasisRanges?: CaptionEmphasisRange[]; wordTimings?: CaptionWordTimings },
>(caption: T, markup: string): T {
  const parsed = parseEmphasisMarkup(markup);
  const next = { ...caption, text: parsed.text };
  if (parsed.ranges.length > 0) next.emphasisRanges = parsed.ranges;
  else delete next.emphasisRanges;
  // 単語ハイライトの対応づけは text の文字オフセットなので、文言が変わったら破棄する
  // (§4.2-B2 の依存規則。normalizeCaptionWordTimings がハッシュ不一致で落とす)
  return normalizeCaptionWordTimings(next);
}

/**
 * テロップ本体の対応づけ (単語ハイライトの wordTimings) を保存形に揃える (§4.2-B2)。
 * `textHash` が現在の text と合わないもの・区間の形が壊れているものはキーごと落とす
 * (保存値 = 実効値)。**入力は変更せず、変化が無ければ同じ参照を返す**。
 */
export function normalizeCaptionWordTimings<T extends { text: string; wordTimings?: CaptionWordTimings }>(
  caption: T,
): T {
  if (caption.wordTimings === undefined) return caption;
  const normalized = normalizeWordTimings(caption.wordTimings, caption.text ?? '');
  const current = caption.wordTimings;
  const same =
    normalized !== undefined
    && normalized.textHash === current.textHash
    && normalized.units.length === current.units.length
    && normalized.units.every((unit, i) => {
      const before = current.units[i];
      return (
        before !== undefined
        && unit.start === before.start
        && unit.end === before.end
        && unit.startSec === before.startSec
        && unit.endSec === before.endSec
      );
    });
  if (same) return caption;
  const next = { ...caption };
  if (normalized) next.wordTimings = normalized;
  else delete next.wordTimings;
  return next;
}

/**
 * 縁取り幅 (px) の自動値。絶対 px ではなくフォントサイズ比にして、解像度・文字サイズが変わっても
 * 同じ見た目を保つ。横型は 5% (46px → 2px) の「細め」、縦型 (スマホ全画面で文字が大きく背景も
 * 賑やか) は 10% (60px → 6px) の太めにする。
 */
/** 帯 (band) の上下余白の既定値 (px): フォントサイズの 50% (CAPTION_RATIOS.bandPadding)。 */
export function defaultBandPaddingPx(fontSizePx: number): number {
  return Math.round(fontSizePx * CAPTION_RATIOS.bandPadding);
}

/** 箱 (box) の角丸の既定値 (px): フォントサイズの 15% (CAPTION_RATIOS.boxRadius)。 */
export function defaultBoxRadiusPx(fontSizePx: number): number {
  return Math.round(fontSizePx * CAPTION_RATIOS.boxRadius);
}

/**
 * テロップブロックの上下余白 (px) を解決する。帯 (band) は backgroundPaddingPx が指定されていればそれ
 * (fit による縮小と同じ比率 scale を掛ける)、未指定・帯以外はフォントサイズの 50%。
 * 帯以外でも余白はブロックの縦位置 (アンカー合わせ) に効くため、既定値は形に関わらず同じにしておく。
 *
 * @param fontSizePx 縮小 (fit) 適用後の実フォントサイズ
 * @param scale fit の縮小率 (1 = 縮小なし)
 */
export function resolveBandPaddingPx(
  design: Pick<CaptionDesign, 'background' | 'backgroundPaddingPx'>,
  fontSizePx: number,
  scale = 1,
): number {
  if (design.background === 'band' && design.backgroundPaddingPx !== undefined) {
    return Math.round(design.backgroundPaddingPx * scale);
  }
  return defaultBandPaddingPx(fontSizePx);
}

export function defaultOutlineWidthPx(fontSizePx: number, portrait = false): number {
  const ratio = portrait ? CAPTION_RATIOS.outlineWidthPortrait : CAPTION_RATIOS.outlineWidthLandscape;
  return Math.max(1, Math.round(fontSizePx * ratio));
}

/**
 * design から実際に描く縁取り幅 (px) を決める。未指定なら自動値 (output が縦型なら太め)、
 * 0 なら縁取りなし。旧データの background="outline" (縁取りを background で表していた頃) は 2px 扱い。
 */
export function resolveOutlineWidthPx(
  design: CaptionDesign,
  output?: { width: number; height: number },
): number {
  if (design.outlineWidthPx !== undefined) return design.outlineWidthPx;
  if (design.background === 'outline') return 2;
  return defaultOutlineWidthPx(design.fontSizePx, output ? isPortraitOutput(output) : false);
}

export const DEFAULT_CAPTION_OUTLINE_JOIN: CaptionOutlineJoin = CAPTION_DEFAULTS.outlineJoin;

/** 縁取りの角の形 (未指定 = round)。 */
export function resolveOutlineJoin(design: Pick<CaptionDesign, 'outlineJoin'>): CaptionOutlineJoin {
  return design.outlineJoin ?? DEFAULT_CAPTION_OUTLINE_JOIN;
}

// ── ドロップシャドウ ──────────────────────────────────────────────

/** 影の角度 (Premiere 流: 0° = 上、時計回り。135° = 右下)。当面は固定。 */
export const CAPTION_SHADOW_ANGLE_DEG: number = CAPTION_RATIOS.shadowAngleDeg;
/** 影の距離のフォントサイズ比 (offset 未指定時)。Premiere の「距離 10」相当の控えめな移動。 */
export const CAPTION_SHADOW_DISTANCE_RATIO: number = CAPTION_RATIOS.shadowDistance;
export const DEFAULT_CAPTION_SHADOW_COLOR: string = CAPTION_DEFAULTS.shadowColor;
export const DEFAULT_CAPTION_SHADOW_OPACITY: number = CAPTION_DEFAULTS.shadowOpacity;

/** 影のぼかしの既定値 (影を有効にしたときの初期値)。フォントサイズの 20% (CAPTION_RATIOS.shadowBlur)。 */
export function defaultShadowBlurPx(fontSizePx: number): number {
  return Math.round(fontSizePx * CAPTION_RATIOS.shadowBlur);
}

/**
 * 影の自動オフセット (shadowOffsetXPx / YPx 未指定時)。角度 CAPTION_SHADOW_ANGLE_DEG (右下)、
 * 距離 fontSizePx × CAPTION_SHADOW_DISTANCE_RATIO を画面座標 (右 +x、下 +y) に変換する。
 * レンダラー (resolveCaptionShadow) と編集 UI の「位置 X / Y」の表示で共有する。
 */
export function defaultShadowOffsetPx(fontSizePx: number): { dx: number; dy: number } {
  const distance = fontSizePx * CAPTION_SHADOW_DISTANCE_RATIO;
  // Premiere 流の角度 (0° = 上、時計回り) を画面座標に変換
  const rad = ((CAPTION_SHADOW_ANGLE_DEG - 90) * Math.PI) / 180;
  return { dx: Math.cos(rad) * distance, dy: Math.sin(rad) * distance };
}

/**
 * CSS カラー (#RGB / #RGBA / #RRGGBB / #RRGGBBAA / rgb() / rgba()) に不透明度を掛けて #RRGGBBAA にする。
 * 元の色にアルファがあればそれも掛ける。解釈できない値はそのまま返す。
 */
export function cssColorWithOpacity(color: string, opacity: number): string {
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  const toHex8 = (r: number, g: number, b: number, a: number) =>
    `#${[r, g, b, Math.round(clamp(a) * 255)].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
  const s = color.trim();
  const hex = /^#([0-9a-f]{3,8})$/i.exec(s);
  if (hex) {
    const h = hex[1]!;
    const expand = (c: string) => parseInt(c.length === 1 ? c + c : c, 16);
    if (h.length === 3 || h.length === 4) {
      const a = h.length === 4 ? expand(h[3]!) / 255 : 1;
      return toHex8(expand(h[0]!), expand(h[1]!), expand(h[2]!), a * opacity);
    }
    if (h.length === 6 || h.length === 8) {
      const a = h.length === 8 ? expand(h.slice(6, 8)) / 255 : 1;
      return toHex8(expand(h.slice(0, 2)), expand(h.slice(2, 4)), expand(h.slice(4, 6)), a * opacity);
    }
    return s;
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(s);
  if (rgb) {
    const a = rgb[4] !== undefined ? Number(rgb[4]) : 1;
    return toHex8(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]), a * opacity);
  }
  return s;
}

export interface ResolvedCaptionShadow {
  /** 不透明度を掛けた色 (#RRGGBBAA)。CSS / canvas にそのまま渡せる。 */
  color: string;
  /** 元の色 (不透明度なし)。 */
  baseColor: string;
  opacity: number;
  blurPx: number;
  sizePx: number;
  /** 画面座標のオフセット (右・下が正)。 */
  dx: number;
  dy: number;
}

/**
 * design の影指定を描画用の値に解決する。影なしなら null。
 * - 有効判定: shadowOpacity > 0。shadowOpacity 未指定の旧データ (AI 出力) は offset か blur が入っていれば
 *   不透明度 1 で有効
 * - オフセット: shadowOffsetXPx / YPx が指定されていればそれ (scale 倍)、無ければ角度 135° 固定で
 *   距離 = fontSizePx × CAPTION_SHADOW_DISTANCE_RATIO
 * - blur / size は px 指定を scale 倍 (セーフエリアに収めるための縮小と連動させる)
 *
 * @param fontSizePx 縮小 (fit) 適用後の実フォントサイズ
 * @param scale 縮小率 (旧データの絶対 px 指定を同じ比率で縮めるため)
 */
export function resolveCaptionShadow(
  design: CaptionDesign,
  fontSizePx: number,
  scale = 1,
): ResolvedCaptionShadow | null {
  const hasOffset = design.shadowOffsetXPx !== undefined || design.shadowOffsetYPx !== undefined;
  const legacyOn =
    (design.shadowOffsetXPx ?? 0) !== 0 || (design.shadowOffsetYPx ?? 0) !== 0 || (design.shadowBlurPx ?? 0) > 0;
  const opacity = design.shadowOpacity ?? (legacyOn ? 1 : 0);
  if (opacity <= 0) return null;

  let dx: number;
  let dy: number;
  if (hasOffset) {
    dx = (design.shadowOffsetXPx ?? 0) * scale;
    dy = (design.shadowOffsetYPx ?? 0) * scale;
  } else {
    ({ dx, dy } = defaultShadowOffsetPx(fontSizePx));
  }
  const baseColor = design.shadowColor ?? DEFAULT_CAPTION_SHADOW_COLOR;
  return {
    color: cssColorWithOpacity(baseColor, opacity),
    baseColor,
    opacity,
    blurPx: (design.shadowBlurPx ?? 0) * scale,
    sizePx: (design.shadowSizePx ?? 0) * scale,
    dx,
    dy,
  };
}

/**
 * テロップの話者。シナリオ取り込み時に scenes.dialogue_lines (発話単位の話者帰属) と
 * narration / telop_in_frame から推定して付ける (mapper/captionSpeaker.ts)。
 * - actor: セリフを言っている俳優 (AI俳優 / Seedance プリセット俳優)。faceImageUrl があれば
 *   エディタのテロップ欄・プロパティに小さな顔写真を出す (動画には焼き込まない)
 * - narration: ナレーション (画面外の語り)
 * - other: それ以外 (俳優に紐付かない登場人物のセリフ、キャッチコピー等)
 * 話者ごとにテロップの文字テンプレートを分ける用途の基礎データでもある。
 */
export const CaptionSpeakerKindSchema = z.enum(['actor', 'narration', 'other']);
export type CaptionSpeakerKind = z.infer<typeof CaptionSpeakerKindSchema>;

export const CaptionSpeakerSchema = z.object({
  kind: CaptionSpeakerKindSchema,
  /** 表示名 (俳優名 / dialogue_lines の話者ラベル)。ナレーションでは省略。 */
  label: z.string().optional(),
  /** 俳優 ID (kind=actor)。actorKind は AI俳優 / Seedance プリセットの区別。 */
  actorId: z.string().nullable().optional(),
  actorKind: z.enum(['actor', 'seedance_preset']).nullable().optional(),
  /** 顔写真の公開 URL (署名不要)。無ければ写真は出さない。 */
  faceImageUrl: z.string().nullable().optional(),
  /** シナリオの登場人物宣言 (generation_metadata.characters) への参照キー (kind=other のとき)。
   *  同一視は label でなくこのキーで行う (改名しても同一人物)。
   *  docs/features/scenario-telop-speaker-plan.md §3.5/§3.9。 */
  characterKey: z.string().nullable().optional(),
});
export type CaptionSpeaker = z.infer<typeof CaptionSpeakerSchema>;

/**
 * 「同じ話者」をまとめるためのキー。俳優は actorId、ナレーションは固定、その他はラベル単位
 * (ラベルの無い「その他」同士も 1 グループ)。テロップデザインの「{話者}にすべて適用」で使う。
 */
export function captionSpeakerKey(speaker: CaptionSpeaker | null | undefined): string {
  if (!speaker) return 'unknown';
  if (speaker.kind === 'actor') return `actor:${speaker.actorId ?? speaker.label ?? ''}`;
  if (speaker.kind === 'narration') return 'narration';
  if (speaker.characterKey) return `character:${speaker.characterKey}`;
  return `other:${speaker.label ?? ''}`;
}

/** 話者の表示名 (UI 用)。 */
export function captionSpeakerLabel(speaker: CaptionSpeaker | null | undefined): string {
  if (!speaker) return '話者未設定';
  if (speaker.kind === 'narration') return 'ナレーション';
  return speaker.label || (speaker.kind === 'actor' ? '俳優' : 'その他');
}

export const CaptionSchema = z.object({
  id: z.string().min(1),
  text: z.string(),
  startSec: z.number().nonnegative(),
  endSec: z.number().nonnegative(),
  style: CaptionStyleSchema.optional(),
  design: CaptionDesignSchema.nullable().optional(),
  speaker: CaptionSpeakerSchema.nullable().optional(),
  // このテロップ1件だけを表示しない (プレビュー・書き出しとも)。削除ではないので文言・デザインは
  // 保持され、解除すれば元に戻る。トラック全体は trackMutes.captions。省略 = 表示する。
  hidden: z.boolean().optional(),
  // テロップ内の部分強調の範囲 (§4.2-I)。text 内のコードポイント単位の半開区間で、重なりなし・昇順。
  // **text に依存する属性**なので text を差し替えたら破棄する (schema/extensionAttributes.ts の
  // dependsOn: ['text'])。どう強調するかは design.emphasis 側。意味は schema/captionEmphasis.ts。
  emphasisRanges: z
    .array(z.object({ start: z.number().int().nonnegative(), end: z.number().int().nonnegative() }))
    .optional(),
  // 単語ハイライト (カラオケ字幕。§4.2-B2) の対応づけ。**コード側の同期工程 (テロップを音声と同期 /
  // おまかせの周ごとの同期) だけが書く導出値**で、AI にも UI にも書かせない。
  // start / end は text 内のコードポイント単位、startSec / endSec は**シーン表示秒**
  // (Caption の startSec / endSec と同じ基準)。**text / 表示区間 / 速度 / 分割に依存する属性**なので、
  // どれかが変わったら破棄して再同期を促す (schema/extensionAttributes.ts の
  // dependsOn: ['text', 'startSec', 'endSec'])。描画側は textHash が現在の text と一致しない
  // かぎり無視する (二重化)。意味は schema/captionWordHighlight.ts。
  wordTimings: z
    .object({
      textHash: z.string(),
      units: z.array(
        z.object({
          start: z.number().int().nonnegative(),
          end: z.number().int().nonnegative(),
          startSec: z.number().nonnegative(),
          endSec: z.number().nonnegative(),
        }),
      ),
    })
    .optional(),
});
export type Caption = z.infer<typeof CaptionSchema>;

// トランジション (シーンのつなぎ) は schema/transition.ts が正本 (種類・方向・長さの範囲・
// 重なりの式・描画パラメータ)。従来ここに定義していた名前は互換のため再輸出する。
export {
  TransitionSchema,
  TransitionTypeSchema,
  TransitionDirectionSchema,
  transitionOverlapSec,
} from './transition';
export type { Transition, TransitionType, TransitionDirection } from './transition';

/**
 * シーン単位の色補正 (§4.1-K)。プロジェクト全体の LookSchema と違い **範囲を縛らない**
 * (DuckingSchema と同じ方針: 壊れた JSON でプロジェクトが開けなくならないよう、
 * クランプ・丸め・「プリセットが不正なら継承へ戻す」は lookCatalog.normalizeItemLook が行う)。
 */
export const ItemLookSchema = z.object({
  preset: z.enum(LOOK_PRESETS as [LookPreset, ...LookPreset[]]).optional(),
  intensity: z.number().optional(),
  /** true = このシーンだけカラーグレーディングを掛けない (全体の look を外す)。 */
  disabled: z.boolean().optional(),
});
export type ItemLook = z.infer<typeof ItemLookSchema>;

/**
 * シーン音声の区間ごとの音量 (audio/audioRegions.ts)。範囲はスキーマで縛らず、前処理の
 * normalizeAudioRegions が壊れた要素だけを落とす (ItemLookSchema と同じ方針)。
 */
export const AudioRegionSchema = z.object({
  id: z.string().min(1),
  startSec: z.number(),
  endSec: z.number(),
  volume: z.number(),
});
export const AudioRegionArraySchema = z.preprocess(
  (raw) => (raw === undefined ? undefined : normalizeAudioRegions(raw)),
  z.array(AudioRegionSchema).optional(),
);
export type { AudioRegion };

export const TimelineItemSchema = z.object({
  id: z.string().min(1),
  clipId: z.string().min(1),
  inSec: z.number().nonnegative(),
  outSec: z.number().positive(),
  // このアイテムの先頭に適用されるトランジション。先頭アイテムでは無視する。
  transition: TransitionSchema.optional(),
  // 再生速度(playbackRate)。1=等倍、>1=早送り、<1=スローモーション。
  // 省略時は 1(等倍)。動画クリップにのみ効き、静止画では無視される。
  // in/out は素材上の秒(トリム範囲)のまま。画面表示尺は (outSec-inSec)/speed になる。
  speed: z.number().positive().optional(),
  // 埋め込み音声(現場音)の音量。0〜1 のリニア値で、省略時は 1(等倍)。動画クリップにのみ効き、
  // 静止画では無視される。ナレーション / BGM には効かない(それらはトラック単位の trackMutes で扱う)。
  volume: z.number().min(0).max(1).optional(),
  // 埋め込み音声のミュート。volume の値は保持したまま無音にする(解除すると元の音量に戻る)。
  muted: z.boolean().optional(),
  // 埋め込み音声のフェードイン / フェードアウト (秒。§4.3-E)。起点はシーンの**表示区間**の両端で、
  // 合計が表示尺を超える分は評価時に比例縮小する (audio/audioCue.ts の clampFadePair)。
  // 省略 / 0 = フェードなし。AI 専用の拡張属性 (UI にコントロールは無い)。
  // 範囲をスキーマで縛らない理由は NarrationCueSchema の volume / fade と同じ。
  audioFadeInSec: z.number().optional(),
  audioFadeOutSec: z.number().optional(),
  // 埋め込み音声の区間ごとの音量 (区間ミュート / 減音)。audio/audioRegions.ts が正本 (意味・数式・正規化)。
  // 秒は**素材秒** (IN / OUT・波形と同じ軸) で、volume 0 = ミュート。シーン全体の音量 × フェード ×
  // 区間のゲインの掛け算で効く。入力は前処理で正規化する (壊れた要素だけ落とし、プロジェクトは開ける)。
  // IN / OUT の外へ出た区間は評価時に切り詰める (itemAudioGainSpans)。
  audioRegions: AudioRegionArraySchema,
  captions: z.array(CaptionSchema).default([]),
  // 採用/除外フラグ。除外してもタイムラインからは外さず保持する。
  excluded: z.boolean().optional().default(false),
  // シーン名 (ユーザーが編集できる表示名)。省略時は参照先クリップの label を使う
  // (同じクリップを分割した複数シーンに別々の名前を付けられるよう、アイテム側に持つ)。
  label: z.string().optional(),
  // ズーム / パン (Ken Burns)。schema/motion.ts が正本 (意味・数式・プリセット値)。
  // 進行 0〜1 の相対指定なので、区間 (in/out)・速度を変えてもそのまま効く。分割時は
  // window (元曲線の評価区間) を切り分けて引き継ぐ。省略 = 動きなし。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  motion: MotionSchema.optional(),
  // オーバーレイ (画像 / ぼかし / モザイクの矩形)。schema/overlay.ts が正本 (意味・数式・正規化)。
  // **このシーンの表示秒**を基準にした区間で、省略 = シーン全体 (テロップの startSec / endSec と
  // 同じ基準)。描画は **motion 層の内側** (映像と一緒にズーム / パンする) で、look はその上から
  // 掛かる。焼き込まれた不自然な文字を隠す救済 (§4.1-G) と商品画像の差し込み (§4.1-F) 用。
  // トリム / 分割 / 速度の扱いはテロップと同じ規則。拡張属性 (AI の値空間 ⊇ UI) の 1 つで、
  // 扱いの宣言は schema/extensionAttributes.ts。
  overlays: OverlayArraySchema.optional(),
  // クロップ / リフレーム (§4.1-J)。schema/crop.ts が正本 (意味・数式・正規化)。
  // **元素材のフレームに対する %** なので、区間 (in/out)・速度・分割とは直交する。
  // 出力の比率は変えず、切り出した矩形を出力へ contain (既定) / cover で収める。
  // 描画は motion 層の内側・look の内側で、シーンのオーバーレイ (出力座標) はこの外。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  crop: CropSchema.optional(),
  // サイズと位置 (縮小 + 配置。docs/features/video-editor-scene-placement-plan.md)。
  // schema/placement.ts が正本。**出力に対する %** の枠 (scale 10〜100) に映像を縮めて置き、
  // x / y は余白の配分 (0 = 左上寄せ・50 = 中央・100 = 右下寄せ)。余白は backgroundColor で塗る。
  // 描画は映像エフェクトの内側・look / motion の外側で、シーンのオーバーレイは枠の内側。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  placement: PlacementSchema.optional(),
  // シーン単位の色補正 (§4.1-K)。省略 = プロジェクトの look を継承、`{disabled: true}` =
  // このシーンだけ補正を外す、`{preset, intensity}` = このシーンだけ別のプリセット。
  // 解決順は lookCatalog.resolveItemLook が正本。**範囲をスキーマで縛らない**
  // (壊れた値でプロジェクトごと開けなくならないよう、正規化は normalizeItemLook が行う)。
  look: ItemLookSchema.optional(),
  // フリーズフレーム (§4.1-L)。schema/freeze.ts が正本 (意味・時間モデル・正規化)。
  // atSourceSec は**素材秒** (in/out・速度を変えても止めるコマがずれない)、holdSec は静止の長さ。
  // 表示尺が holdSec だけ伸びる (itemDisplayDurationSec)。speedRamp とは排他。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  freeze: FreezeSchema.optional(),
  // 速度ランプ (§4.1-L)。schema/speedRamp.ts が正本 (意味・時間モデル・正規化)。
  // fromSpeed → toSpeed へ連続的に速度を変える。**speed とは排他** (ランプがあるとき speed は無視)。
  // ランプ中のシーン音声は鳴らせないので無音になる (既知の制限)。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  speedRamp: SpeedRampSchema.optional(),
  // 映像エフェクト (§4.1-S)。schema/effects.ts が正本 (意味・数式・正規化)。
  // 揺れ / ズームパンチ / フラッシュ / グリッチ / 光漏れ / グレイン / VHS を、**このシーンの表示秒**を
  // 基準にした時間窓 (省略 = シーン全体) に掛ける。トリム / 分割 / 速度の扱いはテロップ・
  // オーバーレイと同じ規則 (実体は schema/timedSpan.ts)。1 シーン 4 つまで・同じ種類は 1 つ。
  // 描画は transform (揺れ / 寄り) が look 層の外側・光学系の層がテロップの下 / look の上。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  effects: EffectArraySchema.optional(),
  // 余白の色。schema/backgroundColor.ts が正本 (意味・正規化)。映像は出力の比率に contain で
  // 収まるので、比率が違うと上下 / 左右に余白が出る。その余白を塗る '#RRGGBB'。省略 = 黒。
  // シナリオの「静止画をそのまま使う」シーン (scenes.video_input_mode='still') では mapper が
  // scenes.still_background_color を写す (静止画でも差し込んだ動画でも)。描画は motion / look / エフェクトの**外側** (シーン層直下)。
  // 拡張属性 (AI の値空間 ⊇ UI) の 1 つ。扱いの宣言は schema/extensionAttributes.ts。
  backgroundColor: z.string().optional(),
  // 出自 (docs/features/video-editor-timeline-lineage-refactoring-plan.md §3)。schema/lineage.ts が正本。
  // 移植 (copyProjectWithLatestScenario) と継続処理 (itemSettingsContinuity) の対応付けはこの欄で行い、
  // clipId は「素材の参照」だけに使う。保存形では必須 (読み込み時の移行 inferTimelineLineage で必ず
  // 埋まる。§3.5)。zod では省略可で受けて validateProject が埋める。
  lineage: LineageSchema.optional(),
});
export type TimelineItem = z.infer<typeof TimelineItemSchema>;

/**
 * そのシーンが「再生順 (excluded を除いた並び) の先頭」か。
 *
 * **先頭シーンのトランジションは再生時に無視される** (remotion/layout.ts の computeTimelineLayout は
 * 採用中のアイテムだけを並べ、その 1 件目には重なりを付けない)。除外中のシーンが前にあると
 * 生のインデックスとはずれるので、トランジションの可否を判断するときは必ずこれを使う (§4.1-H)。
 * backend の edit_ops._is_first_played_item と同じ判定。
 */
export function isFirstPlayedItem(timeline: TimelineItem[], index: number): boolean {
  return timeline.findIndex((item) => !item.excluded) === index;
}

/**
 * 採用中 (excluded でない) のシーンのうち**最後**か。越境テロップの注記 (§3.5) に使う
 * (最後のシーンの越境は続く先が無く、動画の末尾で切れる)。
 * 除外中のシーンは常に false (再生されないので「最後」になりようがない)。
 */
export function isLastPlayedItem(timeline: TimelineItem[], index: number): boolean {
  if (timeline[index]?.excluded) return false;
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    if (!timeline[i]!.excluded) return i === index;
  }
  return false;
}

/** シーンの表示名。アイテム自身の label → クリップの label → clipId 先頭の順で決める。 */
export function timelineItemLabel(item: TimelineItem, clip: SourceClip | undefined): string {
  const own = item.label?.trim();
  if (own) return own;
  return clip?.label || `クリップ ${item.clipId.slice(0, 8)}`;
}

/** 秒数を小数第2位に丸める (保存形の精度。UI の表示・入力は schema/timecode.ts の "MM:SS:FF")。 */
export function roundSec(sec: number): number {
  return Math.round(sec * 100) / 100;
}

/**
 * アイテムの再生速度(未指定・不正値は等倍 1)。
 * **速度ランプ (§4.1-L) があるシーンでは常に 1** (ランプが速度を決めるので `speed` は無視する)。
 * backend の `item_timing.item_speed` と同値。
 */
export function itemSpeed(item: TimelineItem): number {
  // **生の `item.speedRamp` を見ない**: 数値キーの欠けた壊れたランプ (`{}` 等) は
  // `itemSpeedRamp` が「ランプなし」に倒すので、そこで判定しないと backend
  // (`item_timing.item_speed`) と食い違い、表示尺が丸ごとずれる。
  if (itemSpeedRamp(item)) return 1;
  return item.speed && item.speed > 0 ? item.speed : 1;
}

/** アイテムの埋め込み音声の音量設定(0〜1)。ミュート中でも保持している値を返す(スライダー表示用)。 */
export function itemVolumeSetting(item: TimelineItem): number {
  if (typeof item.volume !== 'number' || !Number.isFinite(item.volume)) return 1;
  return Math.min(1, Math.max(0, item.volume));
}

/** アイテムの埋め込み音声の実効音量(0〜1)。ミュート中は 0、未指定は 1。 */
export function itemVolume(item: TimelineItem): number {
  return item.muted ? 0 : itemVolumeSetting(item);
}

/**
 * アイテムの**素材尺** (秒)。トリム区間の長さそのもの (速度・フリーズを含まない)。
 */
export function itemSourceDurationSec(item: TimelineItem): number {
  return Math.max(0, item.outSec - item.inSec);
}

/**
 * アイテムの有効な速度ランプ (§4.1-L)。**speed とは排他**で、ランプがあれば speed は無視する。
 * 素材尺が 0 のときはランプも効かない。
 */
export function itemSpeedRamp(item: TimelineItem): ResolvedSpeedRamp | undefined {
  if (itemSourceDurationSec(item) <= 0) return undefined;
  // 数値キーが欠けた / 壊れた保存データは「ランプなし」に倒す (スキーマでは縛らない方針)
  return resolvedSpeedRamp(item.speedRamp);
}

/**
 * アイテムの有効なフリーズ (§4.1-L)。素材区間の内側へ寄せたうえで返す。
 * **速度ランプと排他**なので、ランプがあるシーンでは無いものとして扱う
 * (書き込み経路では弾くが、壊れたデータを読んだときの最後の防衛線)。
 */
export function itemFreeze(item: TimelineItem): ResolvedFreeze | undefined {
  if (!item.freeze || itemSpeedRamp(item)) return undefined;
  return clampFreezeToRange(item.freeze, item.inSec, item.outSec);
}

/**
 * タイミング属性 (speed / speedRamp / freeze) の保存形を確定させる (§4.1-L)。
 *
 * 1. **排他**: `speedRamp` があるシーンでは `speed` と `freeze` を落とす
 *    (ランプが速度を決めるので speed は無視され、ランプ + フリーズは組み合わせない)
 * 2. **区間へのクランプ**: `freeze.atSourceSec` は**素材秒**なので、区間 (in/out) を変えると
 *    素材区間の外へ出うる。外へ出たままだと保存が 400 で弾かれるので内側へ寄せる
 *    (寄せられない = 区間が空なら落とす)
 *
 * 「保存値 = 実効値」を保つための最後の一手で、UI のフォーム・提案の引き継ぎなど
 * すべての書き込み経路から呼ぶ。backend の `item_timing.resolve_item_timing_exclusivity` と
 * video-editor 側の同名関数と同値。**変更が要るときだけ新しいオブジェクトを返す**
 * (引数は変異させない。変更が無ければ同じインスタンスを返す)。
 */
export function resolveItemTimingExclusivity(item: TimelineItem): TimelineItem {
  // 判定は**解決済みのランプ**で行う (数値キーの欠けた壊れたランプで speed / freeze を
  // 落としてしまうと、実際には効かないランプのために設定が消える)。
  if (itemSpeedRamp(item)) {
    if (item.speed === undefined && item.freeze === undefined) return item;
    const next = { ...item };
    delete next.speed;
    delete next.freeze;
    return next;
  }
  // 効かないランプのキーが残っていると要約・検証で「ランプあり」に見えるので落とす
  // (speed / freeze はそのまま残す)。
  if (item.speedRamp) {
    const next = { ...item };
    delete next.speedRamp;
    return resolveItemTimingExclusivity(next);
  }
  if (!item.freeze) return item;
  const clamped = clampFreezeToRange(item.freeze, item.inSec, item.outSec);
  if (
    clamped
    && clamped.atSourceSec === item.freeze.atSourceSec
    && clamped.holdSec === item.freeze.holdSec
  ) {
    return item;
  }
  const next = { ...item };
  if (clamped) next.freeze = clamped;
  else delete next.freeze;
  return next;
}

/**
 * **アイテムの表示尺 (秒) の正本** (§4.1-L)。総尺・シーンの開始秒・テロップの上限・
 * 書き出しの開始秒・合成フレームの代表時刻など、表示時間軸を使う計算はすべてここを通す。
 *
 * - 速度ランプがあれば `rampDisplayDurationSec` (speed は無視)
 * - 無ければ 素材尺 / speed
 * - フリーズがあれば静止の長さ (holdSec) を足す
 *
 * 同じ式が backend の `item_timing.item_display_duration_sec` と
 * video-editor の同名関数にある (3 実装同値)。
 */
export function itemDisplayDurationSec(item: TimelineItem): number {
  const source = itemSourceDurationSec(item);
  const ramp = itemSpeedRamp(item);
  const base = ramp ? rampDisplayDurationSec(source, ramp) : source / itemSpeed(item);
  const freeze = itemFreeze(item);
  return base + (freeze ? freeze.holdSec : 0);
}

/**
 * フリーズの静止区間 (表示秒)。フリーズが無ければ null。
 * `startSec` = 通常再生が止まる時刻 tA、`endSec` = 再生が再開する時刻 (tA + holdSec)。
 */
export function itemFreezeSpan(
  item: TimelineItem,
): { startSec: number; endSec: number; atSourceSec: number } | null {
  const freeze = itemFreeze(item);
  if (!freeze) return null;
  const startSec = (freeze.atSourceSec - item.inSec) / itemSpeed(item);
  return { startSec, endSec: startSec + freeze.holdSec, atSourceSec: freeze.atSourceSec };
}

/**
 * 表示秒 → 素材秒の写像 (§4.1-L)。合成フレームの代表時刻・分割点の解決に使う。
 * フリーズの静止区間では `atSourceSec` を返す。
 */
export function itemSourceSecAt(item: TimelineItem, displaySec: number): number {
  const source = itemSourceDurationSec(item);
  const ramp = itemSpeedRamp(item);
  const t = Math.max(0, Math.min(itemDisplayDurationSec(item), displaySec));
  if (ramp) return item.inSec + rampSourceSecAt(t, source, ramp);
  const span = itemFreezeSpan(item);
  if (span) {
    if (t < span.startSec) return item.inSec + t * itemSpeed(item);
    if (t < span.endSec) return span.atSourceSec;
    return item.inSec + (t - (span.endSec - span.startSec)) * itemSpeed(item);
  }
  return item.inSec + t * itemSpeed(item);
}

/**
 * 素材秒 → 表示秒の写像 (§4.1-L)。分割候補 (無音・シーンチェンジ) の提示に使う。
 * フリーズ位置より後ろの素材秒は、静止のぶんだけ後ろへずれる (静止の**終わり**を返す)。
 * 速度ランプのある区間は逆関数が閉じた形にならないので、平均速度による近似を返す。
 */
export function itemDisplaySecAtSource(item: TimelineItem, sourceSec: number): number {
  const source = itemSourceDurationSec(item);
  const elapsed = Math.max(0, Math.min(source, sourceSec - item.inSec));
  const ramp = itemSpeedRamp(item);
  if (ramp) {
    const duration = rampDisplayDurationSec(source, ramp);
    return source > 0 ? (elapsed / source) * duration : 0;
  }
  const base = elapsed / itemSpeed(item);
  const span = itemFreezeSpan(item);
  if (span && base >= span.startSec) return base + (span.endSec - span.startSec);
  return base;
}

/**
 * シーン音声の区間ごとの音量 (audioRegions) を、このシーンの**表示秒**の区間に写す (評価用)。
 * IN / OUT の外へ出た部分は切り詰め、空になった区間は落とす。速度・フリーズ・速度ランプは
 * itemDisplaySecAtSource が吸収する。区間が無ければ空配列。
 */
export function itemAudioGainSpans(item: TimelineItem): AudioGainSpan[] {
  const regions = item.audioRegions;
  if (!regions || regions.length === 0) return [];
  const spans: AudioGainSpan[] = [];
  for (const region of regions) {
    const start = Math.max(region.startSec, item.inSec);
    const end = Math.min(region.endSec, item.outSec);
    if (end - start <= 1e-6) continue;
    const displayStart = itemDisplaySecAtSource(item, start);
    const displayEnd = itemDisplaySecAtSource(item, end);
    if (displayEnd - displayStart <= 1e-6) continue;
    spans.push({ startSec: displayStart, endSec: displayEnd, volume: Math.min(1, Math.max(0, region.volume)) });
  }
  return spans;
}

/**
 * アイテムが画面に映る尺(秒)。`itemDisplayDurationSec` の別名 (旧称)。
 * 新しいコードでは `itemDisplayDurationSec` を使うこと。
 */
export function itemOnScreenDurationSec(item: TimelineItem): number {
  return itemDisplayDurationSec(item);
}

/**
 * 再生速度の変更にテロップのタイミングを追従させる。
 *
 * caption.startSec/endSec は「アイテムの画面表示開始を 0 とした画面上の秒」なので、速度を
 * 変えると表示尺が (out-in)/speed に伸縮するのに対し、テロップは元の画面秒に留まり、
 * 素材上の同じ場面からずれてしまう。素材上の同じ位置に留めるには画面秒を
 * (fromSpeed / toSpeed) 倍すればよい (0.5x にすれば 2 倍の時刻に、2x にすれば半分の時刻に)。
 * 結果は roundSec (小数 2 桁) に丸める。
 *
 * **シーン尺を超えた分 (越境。docs/features/video-editor-caption-scene-spillover-plan.md §3.4) は
 * 伸縮させない**。越境ぶんはコンポジション秒 (次のシーンの上に出ている時間) なので、
 * このシーンの速度とは無関係に同じ長さで続く。
 *
 * ```
 * spill     = max(0, endSec - oldDurationSec)   # 越境ぶん。変更前の表示尺で決める
 * inEnd     = min(endSec, oldDurationSec)       # シーン内の終了
 * startSec' = startSec * f,  inEnd' = inEnd * f  (f = fromSpeed / toSpeed)
 * endSec'   = inEnd' + spill
 * ```
 *
 * @param oldDurationSec 速度を変える**前**の表示尺 (itemDisplayDurationSec)。
 */
export function rescaleCaptionsForSpeed(
  captions: TimelineItem['captions'],
  fromSpeed: number,
  toSpeed: number,
  oldDurationSec: number,
): TimelineItem['captions'] {
  const from = fromSpeed > 0 ? fromSpeed : 1;
  const to = toSpeed > 0 ? toSpeed : 1;
  if (from === to) return captions;
  const factor = from / to;
  const oldDur = oldDurationSec > 0 ? oldDurationSec : Infinity;
  return captions.map((caption) => {
    // 越境ぶんは据え置き、シーン内の部分だけを伸縮させる
    const spill = Math.max(0, caption.endSec - oldDur);
    const inEnd = Math.min(caption.endSec, oldDur);
    const next = {
      ...caption,
      startSec: roundSec(caption.startSec * factor),
      endSec: roundSec(inEnd * factor + spill),
    };
    // 速度を変えると表示秒の基準そのものが変わるので、単語ハイライトの対応づけは捨てる
    // (§4.2-B2 の依存規則 onRangeOrSpeedChange: 'drop'。再同期すれば戻る)
    delete next.wordTimings;
    return next;
  });
}

/**
 * シーンの区間 (イン / アウト) を変えたときに、テロップの単語ハイライトの対応づけを捨てる
 * (§4.2-B2 の依存規則)。対応づけを持つテロップが無ければ同じ配列をそのまま返す。
 */
export function dropCaptionWordTimings(captions: TimelineItem['captions']): TimelineItem['captions'] {
  if (!captions.some((caption) => caption.wordTimings !== undefined)) return captions;
  return captions.map((caption) => {
    if (caption.wordTimings === undefined) return caption;
    const next = { ...caption };
    delete next.wordTimings;
    return next;
  });
}

export const SourceClipTypeSchema = z.enum(['video', 'image']);
export type SourceClipType = z.infer<typeof SourceClipTypeSchema>;

export const SourceClipSchema = z.object({
  /** 同じシーン用に生成した別テイクを AI が比較するためのグループ。旧プロジェクトでは未設定。 */
  sourceSceneId: z.string().optional(),
  id: z.string().min(1),
  // 素材種別。既存プロジェクト JSON との後方互換のため省略時は "video"。
  type: SourceClipTypeSchema.default('video'),
  // image-generator の素材参照。temporary_materials / materials の ID。
  // placeholder クリップ (下記) だけは素材を持たないため省略する。それ以外は必須 (superRefine)。
  materialId: z.string().min(1).optional(),
  // 素材の無いシーンの枠 (黒画面)。mapper が「動画もフレーム画像も無いシーン」を
  // タイムラインから落とさず、尺・ナレーション・テロップの枠として並べるために作る
  // (プレビュー動画 mp4_composer が同じ状況で黒画面クリップを置くのと同じ扱い)。
  // type='image' 固定・materialId 無し・src 無し。プレビューは黒 (EditVideo は src 無しを描かない)、
  // レンダリングは backend render_service が黒画像の data URI を src に充填する。
  // 解析・書き出し (XML/ZIP) の対象外。
  placeholder: z.boolean().optional(),
  // true = 個人フォルダ (temporary_materials) / false = 共有フォルダ (materials)。
  // 署名URL解決時にフォールバックした結果で更新され、次回保存で永続化される。
  isTemporary: z.boolean().default(true),
  // 導出値: ロード時に充填する署名付き URL。DB には保存しない (stripResolvedUrls)。
  src: z.string().optional(),
  proxySrc: z.string().nullable().optional(),
  // 静止画の場合は「タイムラインで使える最大表示尺」を表す(ファイル固有の尺は持たないため)。
  durationSec: z.number().positive(),
  // **出力 (project.output) と同じ寸法**が入る (mapper/buildEditProjectFromScenario が output で
  // 埋める。素材の実寸ではない)。レイアウトの基準としてのみ使うこと。
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  fps: z.number().positive(),
  // **素材そのものの実寸** (§4.1-J)。解析 (analysis.metadata) と素材行 (materials.width/height)
  // から充填する導出値で、取れていないクリップでは undefined になる。
  // クロップ (crop) の計算は素材に対する % なのでこの実寸が必須で、無いクリップでは
  // クロップ層を足さない・AI にも書かせない (sourceClipDimensions)。
  srcWidth: z.number().int().positive().optional(),
  srcHeight: z.number().int().positive().optional(),
  label: z.string().nullable().optional(),
  // image-generator 側の生成プロンプト。
  generationPrompt: z.string().nullable().optional(),
  // シナリオ由来のナレーション(またはサウンドロゴ)音声の素材参照。
  narrationMaterialId: z.string().nullable().optional(),
  narrationIsTemporary: z.boolean().optional(),
  // 導出値: ロード時に充填する署名付き URL。DB には保存しない。
  narrationSrc: z.string().nullable().optional(),
  narrationText: z.string().nullable().optional(),
  narrationDurationSec: z.number().nonnegative().nullable().optional(),
  // シナリオ由来のテロップ原稿 (scene.telop)。AI はこの文言をそのままテロップに使う。
  scriptText: z.string().nullable().optional(),
  // scriptText を行ごとに分けたもの (splitTelopLines と同じトリム・空行除去規則) + 行ごとの
  // 話者推定 (mapper/captionSpeaker.ts)。AI 編集提案は timeline を丸ごと差し替えるため、
  // 提案後のテロップにはこの話者情報が付いていない。適用時に提案テロップの文言をこれと
  // 突き合わせて話者を復元する (proposal/captionContinuity.ts)。telop が空なら null。
  scriptLines: z
    .array(z.object({ text: z.string(), speaker: CaptionSpeakerSchema.nullable() }))
    .nullable()
    .optional(),
  // クリップ解析結果。フェーズ1では緩く unknown のまま格納する。
  analysis: z.unknown().nullable().optional(),
  // 音声の出自 (lineage 計画 §5.1)。音声クリップ (ナレーションの音声キャリア等) にだけ使う。
  // 'scenario' = 組み立て時のクリップ、'user' = 利用者がピッカーから足したキャリア、
  // 'unknown' = 旧データで判別できないもの (§5.5。移植では素材 ID で対応付けて救済する)。
  // 項目の lineage.source と同じ意味。通常モードのシーンクリップは sourceSceneId を持つので
  // 省略 = 'scenario' として扱う。
  audioSource: z.enum(['scenario', 'user', 'unknown']).optional(),
  // audioSource を旧データから推定で埋めた印 (§5.5)。
  audioSourceInferred: z.boolean().optional(),
}).superRefine((clip, ctx) => {
  if (!clip.placeholder && !clip.materialId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['materialId'], message: 'materialId is required unless placeholder' });
  }
  if (clip.placeholder && clip.type !== 'image') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['type'], message: 'placeholder clip must be type=image' });
  }
});
export type SourceClip = z.infer<typeof SourceClipSchema>;

/** 正の数のペアだけを整数に丸めて返す (backend の crop.py と同じ規則)。 */
function positiveSizePair(width: unknown, height: unknown): { width: number; height: number } | null {
  if (typeof width !== 'number' || typeof height !== 'number') return null;
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (!(width > 0) || !(height > 0)) return null;
  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * 素材そのものの実寸 (§4.1-J)。
 *
 * `SourceClip.width` / `height` は**出力寸法**が入っている (mapper が `output` で埋める) ので、
 * クロップの計算には使えない。実寸が取れていないクリップではクロップを掛けない
 * (プレビュー・MP4・書き出し・AI 契約のすべてでこの関数を基準にする)。
 *
 * 参照順:
 * 1. `srcWidth` / `srcHeight` (解析結果から保存した値・署名 URL 解決で補った値)
 * 2. `analysis.metadata.width` / `height` — **`srcWidth` を持たない既存プロジェクト**向けの
 *    フォールバック (解析済みなら実寸はここにある。これが無いと解析済みのプロジェクトまで
 *    「クロップ不可」になってしまう)
 */
export function sourceClipDimensions(
  clip: Pick<SourceClip, 'srcWidth' | 'srcHeight' | 'analysis'> | null | undefined,
): { width: number; height: number } | null {
  const direct = positiveSizePair(clip?.srcWidth, clip?.srcHeight);
  if (direct) return direct;
  const analysis = clip?.analysis as { metadata?: { width?: unknown; height?: unknown } } | null | undefined;
  const metadata = analysis && typeof analysis === 'object' ? analysis.metadata : undefined;
  if (!metadata || typeof metadata !== 'object') return null;
  return positiveSizePair(metadata.width, metadata.height);
}

/** 素材の無いプレースホルダークリップ (黒画面の枠) か。 */
export function isPlaceholderClip(clip: Pick<SourceClip, 'placeholder' | 'materialId'>): boolean {
  return clip.placeholder === true || !clip.materialId;
}

/**
 * ナレーション音声のキャリアクリップか。mapper (buildEditProjectFromScenario) は、シーンのナレーション
 * 音声を narrationTrack の cue から clipId 経由で参照するためだけに、materialId = narrationMaterialId =
 * 音声素材 の type='video' クリップを作る (タイムラインには置かれない)。映像を持たないので、
 * 解析の要否判定やフレーム抽出の対象から外す (backend executors.is_narration_carrier_clip と同じ判定)。
 */
export function isNarrationCarrierClip(clip: Pick<SourceClip, 'materialId' | 'narrationMaterialId'>): boolean {
  return clip.narrationMaterialId != null && clip.materialId === clip.narrationMaterialId;
}

/**
 * 音声キュー。BGM トラック (`bgmTrack`) と将来の SE トラック (`seTrack`) で共有する
 * (docs/features/video-editor-bgm-track-plan.md §5.1)。
 *
 * BGM と SE の差はスキーマではなく振る舞い (長尺ループ vs 一発) なので、レンダラー・
 * 書き出し・正規化を 1 本にできるようにキューの型は共通にしてある。
 * 周回・分割・トラック割り当ての式は audio/audioCue.ts が正本 (§6.1 / §8.2 / §7)。
 */
export const AudioCueSchema = z.object({
  id: z.string().min(1),
  // image-generator の素材参照 (SourceClip / 旧 Bgm と同じ)。
  // **optional なのは AI の周回中のプレースホルダー SE を parse で落とさないため** (§4.3-D):
  // 実況 (§19.2) のパッチには、まだ組織の素材へ複製していない効果音 (catalogId だけを持つ cue) が
  // 含まれることがある。必須にすると zod の parse が先に throw して `dropUnusableSeCues` まで
  // 届かず、パッチ丸ごと (= その周の他の変更も) 捨てられてしまう。
  // **保存されるキューは常に materialId を持つ** (BE の validate_edit_project_json が必須にする)。
  // レンダラー (video-editor/src/schema/project.ts) の AudioCue も同じく optional。
  materialId: z.string().min(1).optional(),
  isTemporary: z.boolean().default(true),
  // 導出値: ロード時に充填する署名付き URL。DB には保存しない。
  src: z.string().optional(),
  // タイムライン側: 鳴らす区間(動画全体の絶対秒)。区間の外は完全な無音になるため、
  // 冒頭を現場音だけで見せたり、エンドカードのサウンドロゴを無音の中で立たせたりできる。
  // endSec 省略/null = 動画の終わりまで (BGM) / 素材の実尺まで (loop: false + sourceDurationSec)。
  startSec: z.number().nonnegative(),
  endSec: z.number().nonnegative().nullable().optional(),
  // 曲側: 初回の再生開始点(素材の秒)。省略 = 0(曲頭)。周回 (loop) は常に曲頭 0 から
  // 素材の終わりまでを繰り返すので、この値は初回の片にしか効かない(§6.1)。
  sourceStartSec: z.number().nonnegative().optional(),
  // 素材の実尺(秒)。挿入時に写し、旧キューは署名 URL の充填時に補う。
  // 周回の展開・SE の endSec 省略・分割の位相計算・書き出しのクリップ長で使う。
  sourceDurationSec: z.number().positive().optional(),
  volume: z.number().min(0).max(1),
  // フェードイン / フェードアウトの秒数。0 または省略 = なし。
  // 起点は動画の両端ではなくキュー区間の両端(startSec / endSec)。
  fadeInSec: z.number().nonnegative().optional(),
  fadeOutSec: z.number().nonnegative().optional(),
  // 区間より素材が短いときに繰り返すか。省略 = true(BGM の既定)。SE は false で挿入する。
  loop: z.boolean().optional(),
  // このキューだけ鳴らさない。トラック全体は trackMutes。
  muted: z.boolean().optional(),
  // 再生速度 (音程は保つ)。省略 = 1 (等倍)。UI は SE にだけ出す (0.5〜2倍)。
  // 区間・周回の長さはタイムライン秒 (素材尺 ÷ speed)、入り点 (sourceStartSec) は素材秒のまま。
  // 範囲外の値はここでは弾かず、audio/audioCue.ts の audioCueSpeed が 0.5〜2 に寄せる。
  speed: z.number().positive().optional(),
  // 置き場所の正 (lineage 計画 §4.1)。schema/timeAnchorSchema.ts が正本、引き直しは schema/timeAnchor.ts。
  // startSec / endSec は錨から導出したキャッシュで、保存時に必ず最新に直す (レンダラーと書き出しは
  // startSec / endSec だけを読む)。未移行かどうかは project の formatVersion で判定する (§4.7。キーの
  // 有無では判定しない)。移行後は常に存在する。
  anchor: TimeAnchorFieldSchema,
  // 明示された終了秒 (endSec が数値) の置き場所 (BGM だけ。SE は長さを保つので持たない)。
  // endSec が省略 / null (動画の終わりまで・素材の実尺まで) のときは持たない。
  anchorEnd: TimeAnchorFieldSchema,
  // 【導出値・毎回計算・SE のみ】鳴らさない (= 配置先の項目が無い: 削除 / 除外中)。BGM には立てない
  // (継続再生。§4.2 手順 7・§4.5)。正規化のたびに計算し直す (保存形にも書く。レンダラー・BE 素材解決・
  // 書き出しはこれだけを読む)。
  silenced: z.boolean().optional(),
});
export type AudioCue = z.infer<typeof AudioCueSchema>;

/**
 * 旧 `project.bgm`(単一 BGM)の形。
 *
 * @deprecated `bgmTrack: AudioCue[]` に移行済み (§5.3 (a))。読み込み時に
 * `migrateProjectBgmTrack` が 1 キューへ変換するので、**新規コードでは使わない**。
 * 保存時に `bgm` キーは書かない。
 */
export const LegacyBgmSchema = z.object({
  materialId: z.string().min(1),
  isTemporary: z.boolean().default(true),
  src: z.string().optional(),
  volume: z.number().min(0).max(1),
  startSec: z.number().nonnegative().optional(),
  endSec: z.number().nonnegative().nullable().optional(),
  fadeInSec: z.number().nonnegative().optional(),
  fadeOutSec: z.number().nonnegative().optional(),
});
export type LegacyBgm = z.infer<typeof LegacyBgmSchema>;

/**
 * @deprecated 旧名。`AudioCue` (bgmTrack のキュー) に置き換わった。
 * 参照が残っている箇所を移行するまでの互換 export。
 */
export type Bgm = LegacyBgm;

/**
 * AI に曲替えをさせるための候補曲 (§9.4)。描画にも書き出しにも使わない、AI 提示用の軽い配列。
 * 実体は素材 (temporary_materials / materials) なので、キュー側は materialId だけを持つ。
 */
export const BgmCandidateSchema = z.object({
  materialId: z.string().min(1),
  isTemporary: z.boolean().default(true),
  /** 曲名 (素材の name)。UI と AI への提示に使う */
  name: z.string().optional(),
  /** 素材の実尺 (秒)。キューへ入れるときの sourceDurationSec の元 */
  durationSec: z.number().positive().optional(),
});
export type BgmCandidate = z.infer<typeof BgmCandidateSchema>;

/**
 * BGM 1 曲のビート検出結果 (T ビート検出とビート同期。§4.3-T)。
 *
 * 素材 (`materialId`) ごとに 1 件持つ**導出値**で、書くのは解析ジョブ (と AI 実行前の補完) だけ。
 * UI も AI も直接は書かない。描画・書き出しには一切使わず、カット点 / 効果音 / 映像エフェクトを
 * 拍へ吸着させる (`snap_to_beats`) ためだけに読む。
 *
 * - `beats`: 音源の先頭からの秒 (昇順・小数 3 桁)。**素材秒**であって動画の絶対秒ではない
 *   (キューの `sourceStartSec` とループの折り返しを通して絶対秒へ写すのは `audio/beatSync.ts`)
 * - `confidence`: 0〜1。低い曲 (拍の無い環境音など) は UI で薄く出し、lint も発火させない
 * - `analyzedSec`: 実際に解析した長さ (先頭から最大 10 分)
 *
 * onset-dp-v2 (video-editor の src/analysis/timingAnalysis.ts・docs/features/brand-cut-motion-graphics-plan.md
 * §15.2 手順 3) はモーションの BGM 同期のために次を足す (v1 の結果には無いので optional。編集画面は今は使わない):
 * - `meter`: 拍子 (初期対応は '4/4' のみ)
 * - `downbeats`: 小節頭 (素材秒・`beats` の部分列)。`downbeatConfidence` は拍の `confidence` とは別の信頼度
 * - `bars`: 小節ごとの区間とエネルギー (0〜1)・`energy`: 拍ごとのエネルギー (`beats` と同じ長さ)
 * - `accents`: 強いオンセットの拍 (素材秒)・`sections`: エネルギーの変化点の小節頭 (素材秒)
 */
export const BgmAnalysisSchema = z.object({
  tempoBpm: z.number().nonnegative(),
  beats: z.array(z.number().nonnegative()),
  confidence: z.number().min(0).max(1),
  analyzedSec: z.number().nonnegative(),
  method: z.string(),
  /** 解析した時刻 (ISO 8601)。再解析の要否を人が追うためだけの情報。 */
  analyzedAt: z.string().optional(),
  meter: z.string().optional(),
  downbeats: z.array(z.number().nonnegative()).optional(),
  downbeatConfidence: z.number().min(0).max(1).optional(),
  bars: z
    .array(
      z.object({
        startSec: z.number().nonnegative(),
        endSec: z.number().nonnegative(),
        energy: z.number().min(0).max(1),
      }),
    )
    .optional(),
  energy: z.array(z.number().min(0).max(1)).optional(),
  accents: z.array(z.number().nonnegative()).optional(),
  sections: z.array(z.number().nonnegative()).optional(),
});
export type BgmAnalysis = z.infer<typeof BgmAnalysisSchema>;

/**
 * 壊れた `bgmAnalysis` の要素だけを落とす (§0-2 の「落として注記」)。
 *
 * 1 曲ぶんの壊れた解析結果でプロジェクトごと開けなくなるのは割に合わない。
 * 3 実装 (FE / レンダラー / backend の `beat_sync.bgm_analysis_map`) で同じ規則。
 */
export function dropUnusableBgmAnalysis(raw: unknown): unknown {
  if (raw == null) return undefined;
  if (typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, unknown> = {};
  let dropped = 0;
  for (const [materialId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!materialId) continue;
    if (BgmAnalysisSchema.safeParse(value).success) out[materialId] = value;
    else dropped += 1;
  }
  if (dropped > 0) {
    console.warn(`[videoEditor] 読み込めない BGM のビート解析 ${dropped} 件を除外しました`);
  }
  return out;
}

/** `materialId` → ビート検出結果。壊れた要素は読み込み時に落とす。 */
export const BgmAnalysisMapSchema = z.preprocess(
  dropUnusableBgmAnalysis,
  z.record(BgmAnalysisSchema).optional(),
);

/**
 * AI が効果音を置けるようにするための候補 SE (docs/features/video-editor-capability-gaps-plan.md
 * §4.3-D)。BgmCandidate と同じく、描画にも書き出しにも使わない AI / UI 提示用の軽い配列。
 *
 * 候補の出どころは (a) 組織の音声素材のうち既定用途が「効果音」(`default_audio_role='sound_effect'`)
 * のもの と (b) 運営提供カタログ (`audio_catalog_items`)。カタログの音は**使用時にプロジェクトの
 * 一時素材へ複製する**方針 (BGM 計画 §10 の決定) なので、この配列に載る時点では必ず `materialId`
 * を持つ。`catalogId` は複製元の記録で、AI がカタログ ID で指してきたときの別名にもなる。
 */
export const SeCandidateSchema = z.object({
  materialId: z.string().min(1),
  isTemporary: z.boolean().default(true),
  /** 効果音の名前 (素材の name / カタログの name)。UI と AI への提示に使う */
  name: z.string().optional(),
  /** 素材の実尺 (秒)。キューへ入れるときの sourceDurationSec の元 */
  durationSec: z.number().positive().optional(),
  /** 'material' = 組織の音声素材 / 'catalog' = 運営提供カタログから複製したもの */
  source: z.enum(['material', 'catalog']).optional(),
  /** 複製元の audio_catalog_items.id ('catalog' のときだけ) */
  catalogId: z.string().optional(),
});
export type SeCandidate = z.infer<typeof SeCandidateSchema>;

// タイムライン全体を貫くナレーショントラックの1キュー。カットの切り替えとは独立に、
// 動画全体の絶対秒で配置する(長いナレーションが複数カットにまたがってよい)。
// 音声・台本は clipId が指す SourceClip の narrationMaterialId / narrationText を参照する。
export const NarrationCueSchema = z.object({
  id: z.string().min(1),
  clipId: z.string().min(1),
  /** 動画全体における再生開始秒 */
  startSec: z.number().nonnegative(),
  // このナレーション1本だけを鳴らさない (プレビュー・書き出しとも)。トラック全体は
  // trackMutes.narration で扱う。省略 = 鳴らす。
  muted: z.boolean().optional(),
  // このナレーション 1 本だけの音量 (0〜1 のリニア値。省略 = 1 = そのまま) と
  // フェードイン / フェードアウト (秒。省略 / 0 = なし。§4.3-E)。
  // 起点は音源の実尺 (narrationDurationSec) の両端で、合計が実尺を超える分は評価時に比例縮小する。
  // 拡張属性 (AI の値空間 ⊇ UI): UI にあるのは音量スライダーだけで、フェード秒は AI 専用。
  //
  // **範囲はスキーマで縛らない** (§0-2 の「落として注記」と同じ方針): 保存時の範囲検証は
  // backend の validate_edit_project_json が担い、読み込み側は範囲外を throw せず
  // validateProject がクランプして console.warn する (旧データや外部から直接書き換えられた
  // JSON で、1 本の壊れた cue のためにプロジェクトが二度と開けなくなるのを避ける)。
  volume: z.number().optional(),
  fadeInSec: z.number().optional(),
  fadeOutSec: z.number().optional(),
  // 鳴らす長さ (秒。音源の頭から数える)。音源の途中で切りたいときだけ持つ。省略 = 音源の終わりまで。
  // 開始秒からの相対で持つので、cue を動かしても (DnD / ▲▼ / リップル) 切る位置はそのまま付いてくる。
  // 実尺 (narrationDurationSec) より長い値は実尺で頭打ちにする (audio/ducking.ts の narrationCuePlaySec)。
  // UI はインスペクタの終了秒 (= startSec + 鳴る長さ)。正の有限値以外は validateProject が落とす。
  durationSec: z.number().optional(),
  // 入り点 (秒。音源の頭から数える)。音源の頭を切り詰めるときだけ持つ。省略 = 0 = 頭から。
  // 鳴る長さは min(実尺, durationSec) - sourceStartSec (audio/ducking.ts の narrationCuePlaySec)。
  // UI は波形トリム (NarrationInspector) の左のつまみで、動かすと開始秒 (startSec) も同じだけ動く
  // (音声が動画のどこで鳴るかは変えずに頭だけ落とす)。0 以上の有限値以外は validateProject が落とす。
  sourceStartSec: z.number().optional(),
  // 読み上げ文の上書き (表示用)。省略 = クリップの narrationText (生成時の読み上げ文)。分割した本ごとに
  // 違う文言を持たせたり、カードで書き直したりするときだけ持つ (narrationCueText で解決)。
  // 音声そのものは変わらない (再生成はシナリオ側)。空文字は validateProject がキーごと落とす。
  text: z.string().optional(),
  // 置き場所の正 (lineage 計画 §4.1)。組み立て時は {item, そのシーンの項目, 0}。startSec はここから
  // 導出したキャッシュ (schema/timeAnchor.ts)。終端は鳴る長さ (durationSec) で音源相対なので anchorEnd は無い。
  anchor: TimeAnchorFieldSchema,
  // 【永続】音声の出自がシナリオから消えた (ナレーション削除 / シーン削除で移植が立てる。§5.2)。
  // 利用者が cue を削除するか、別の音声を選び直すまで外れない。配置先が戻っても外れない。
  sourceRemoved: z.boolean().optional(),
  // 【導出値・毎回計算】鳴らさない (= 配置先の項目が無い (削除 / 除外中) または sourceRemoved)。
  // 正規化のたびに計算し直す (保存形にも書く。レンダラー・BE 素材解決・書き出しはこれだけを読む)。
  silenced: z.boolean().optional(),
});
export type NarrationCue = z.infer<typeof NarrationCueSchema>;

/** ナレーション cue の読み上げ文 (表示用)。cue の上書き (text) があればそれ、無ければクリップの narrationText。無ければ null。 */
export function narrationCueText(
  cue: Pick<NarrationCue, 'text'>,
  clip: Pick<SourceClip, 'narrationText'> | undefined,
): string | null {
  const override = typeof cue.text === 'string' ? cue.text.trim() : '';
  if (override) return override;
  const base = clip?.narrationText?.trim();
  return base ? base : null;
}

/**
 * 保存データの音量 / フェード秒を「実際に効く範囲」へ寄せる (§4.3-E)。
 *
 * 保存時の検証は backend の `validate_edit_project_json` が担う前提だが、旧データや外部から
 * 直接書き換えられた JSON でプロジェクトが開けなくなると復旧できないので、**読み込み側は
 * throw せずクランプして注記する** (§0-2 の「落として注記」と同じ方針)。
 * 戻り値は「クランプした値 (変化が無ければ undefined)」。
 */
function clampAudioSetting(
  value: number | undefined,
  min: number,
  max: number,
  label: string,
): { value: number | undefined; changed: boolean } {
  if (value === undefined) return { value: undefined, changed: false };
  if (!Number.isFinite(value)) {
    warnClampedAudioSetting(label, `${label} が数値ではないため無視しました`);
    return { value: undefined, changed: true };
  }
  const clamped = Math.min(max, Math.max(min, value));
  if (clamped !== value) {
    warnClampedAudioSetting(label, `${label} (${value}) が範囲 ${min}〜${max} の外のため ${clamped} に丸めました`);
    return { value: clamped, changed: true };
  }
  return { value, changed: false };
}

/**
 * 同じ注記でコンソールを溢れさせない。**キーはラベル (どの cue のどの項目か) だけ**にして、
 * 値違いの同じ項目を何度も出さない。長いタイムラインで Set が際限なく膨らまないよう
 * 件数にも上限を置く (超えたら以降は注記しない。読む人は最初の数十件で十分に気づける)。
 */
const MAX_AUDIO_SETTING_WARNINGS = 50;
const warnedAudioSettingLabels = new Set<string>();
function warnClampedAudioSetting(label: string, message: string): void {
  if (warnedAudioSettingLabels.has(label)) return;
  if (warnedAudioSettingLabels.size >= MAX_AUDIO_SETTING_WARNINGS) return;
  warnedAudioSettingLabels.add(label);
  console.warn(`[videoEditor] ${message}`);
}

/** ナレーション cue の音量 / フェードを範囲内に寄せ、壊れた鳴らす長さ / 入り点を落とす (変化が無ければ同じ参照を返す)。 */
export function clampNarrationCueAudio(cue: NarrationCue, label: string): NarrationCue {
  const volume = clampAudioSetting(cue.volume, 0, 1, `${label}.volume`);
  const fadeIn = clampAudioSetting(cue.fadeInSec, 0, MAX_AUDIO_FADE_SEC, `${label}.fadeInSec`);
  const fadeOut = clampAudioSetting(cue.fadeOutSec, 0, MAX_AUDIO_FADE_SEC, `${label}.fadeOutSec`);
  const durationDropped =
    cue.durationSec !== undefined && !(Number.isFinite(cue.durationSec) && cue.durationSec > 0);
  if (durationDropped) warnClampedAudioSetting(`${label}.durationSec`, `${label}.durationSec が正の数ではないため無視しました`);
  // 入り点は 0 以上の有限値。0 はキーごと落とす (= 頭から)。出点 (durationSec) 以上も落とす (鳴る長さが無くなる)
  const sourceStartDropped =
    cue.sourceStartSec !== undefined &&
    !(
      Number.isFinite(cue.sourceStartSec) &&
      cue.sourceStartSec > 0 &&
      (durationDropped || cue.durationSec === undefined || cue.sourceStartSec < cue.durationSec)
    );
  if (sourceStartDropped && cue.sourceStartSec !== 0) {
    warnClampedAudioSetting(`${label}.sourceStartSec`, `${label}.sourceStartSec が 0 以上かつ出点より前ではないため無視しました`);
  }
  // 読み上げ文の上書きは空白だけならキーごと落とす (クリップの文言に戻る)
  const textDropped = cue.text !== undefined && cue.text.trim() === '';
  if (!volume.changed && !fadeIn.changed && !fadeOut.changed && !durationDropped && !sourceStartDropped && !textDropped) return cue;
  const next = { ...cue };
  if (durationDropped) delete next.durationSec;
  if (sourceStartDropped) delete next.sourceStartSec;
  if (textDropped) delete next.text;
  if (volume.value === undefined) delete next.volume;
  else next.volume = volume.value;
  if (fadeIn.value === undefined) delete next.fadeInSec;
  else next.fadeInSec = fadeIn.value;
  if (fadeOut.value === undefined) delete next.fadeOutSec;
  else next.fadeOutSec = fadeOut.value;
  return next;
}

/** シーン音声のフェードを範囲内に寄せる (変化が無ければ同じ参照を返す)。 */
export function clampItemAudioFades(item: TimelineItem, label: string): TimelineItem {
  const fadeIn = clampAudioSetting(item.audioFadeInSec, 0, MAX_AUDIO_FADE_SEC, `${label}.audioFadeInSec`);
  const fadeOut = clampAudioSetting(item.audioFadeOutSec, 0, MAX_AUDIO_FADE_SEC, `${label}.audioFadeOutSec`);
  if (!fadeIn.changed && !fadeOut.changed) return item;
  const next = { ...item };
  if (fadeIn.value === undefined) delete next.audioFadeInSec;
  else next.audioFadeInSec = fadeIn.value;
  if (fadeOut.value === undefined) delete next.audioFadeOutSec;
  else next.audioFadeOutSec = fadeOut.value;
  return next;
}

/**
 * シーン音声の区間ごとの音量を IN / OUT の内側へ切り詰める (変化が無ければ同じ参照を返す)。
 * 読み込み時の防衛線。保存時の検証は backend の validate_edit_project_json。
 */
export function clampItemAudioRegions(item: TimelineItem): TimelineItem {
  if (!item.audioRegions) return item;
  const next = normalizeAudioRegions(item.audioRegions, { inSec: item.inSec, outSec: item.outSec });
  if (audioRegionsEqual(next, item.audioRegions)) return item;
  const copy = { ...item };
  if (next) copy.audioRegions = next;
  else delete copy.audioRegions;
  return copy;
}

/** ナレーション cue の実効音量 (未指定・不正値は等倍 1。ミュートは 0)。 */
export function narrationCueVolume(cue: Pick<NarrationCue, 'volume' | 'muted'>): number {
  if (cue.muted) return 0;
  const volume = cue.volume;
  if (typeof volume !== 'number' || !Number.isFinite(volume)) return 1;
  return Math.min(1, Math.max(0, volume));
}

export const OutputSettingsSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  fps: z.number().positive(),
});
export type OutputSettings = z.infer<typeof OutputSettingsSchema>;

export const HistoryActorSchema = z.enum(['ai', 'user']);
export type HistoryActor = z.infer<typeof HistoryActorSchema>;

/**
 * 「どの時点のシナリオから組み立てたか」のスナップショット (sourceSnapshot)。編集を開いたときに
 * 現在のシナリオと突き合わせ、作成後にシーンの素材・テロップ・ナレーション・BGM が変わっていれば
 * 「最新シナリオを取り込んで複製」を案内する (mapper/sourceSnapshot.ts)。描画・書き出しには使わない。
 * 旧プロジェクト JSON には無い (undefined) ので、そのときは作成時刻とシーンの更新時刻の比較に落ちる。
 * 範囲は縛らない (壊れていてもプロジェクトが開けなくならないよう、検知側で欠けを許容する)。
 */
export const SourceSnapshotSceneSchema = z.object({
  sceneId: z.string(),
  orderNumber: z.number(),
  videoMaterialId: z.string().nullable(),
  imageMaterialId: z.string().nullable(),
  /** 前面音声 (ナレーション、またはサウンドロゴ) の素材 ID。 */
  narrationMaterialId: z.string().nullable(),
  telop: z.string(),
  narration: z.string(),
});
export type SourceSnapshotScene = z.infer<typeof SourceSnapshotSceneSchema>;

export const SourceSnapshotSchema = z.object({
  scenarioId: z.string(),
  scenarioUpdatedAt: z.string().nullable(),
  frameMode: z.string().nullable(),
  bgmMaterialId: z.string().nullable(),
  scenes: z.array(SourceSnapshotSceneSchema),
});
export type SourceSnapshot = z.infer<typeof SourceSnapshotSchema>;

export const HistoryEntrySchema = z.object({
  timestamp: z.string(),
  actor: HistoryActorSchema,
  action: z.string(),
  detail: z.unknown().optional(),
});
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

export const TrackMutesSchema = z.object({
  captions: z.boolean().optional(),
  narration: z.boolean().optional(),
  bgm: z.boolean().optional(),
  // SE (効果音) トラック。§4.3-D で実装済み (TimelinePanel の「効果音」行ラベルで切り替える)。
  se: z.boolean().optional(),
});
export type TrackMutes = z.infer<typeof TrackMutesSchema>;
export type TrackMuteKey = keyof TrackMutes;

/**
 * カラーグレーディング (プリセット)。プリセットの値・表示名・パラメータの正本は
 * schema/lookCatalog.ts (このスキーマは値の制約のみを持つ。lookCatalog.LookSetting と構造互換)。
 * - preset: LOOK_PRESETS のいずれか
 * - intensity: 強度 0〜1。省略時 1 (lookCatalog.DEFAULT_LOOK_INTENSITY)
 * - enabled: false = 設定は残すが掛けない (UI のスイッチ OFF)。省略時 true
 */
/**
 * ナレーション中に BGM を自動で下げる「ダッキング」の設定 (プロジェクト単位。§4.3-E)。
 * 意味・数式・既定値・クランプの正本は audio/ducking.ts (3 実装で共有する純関数)。
 *
 * ここでは**範囲を厳しく縛らない**: 旧データや外部から書き換えられた JSON でプロジェクト全体が
 * 開けなくなるのを避け、範囲のクランプと丸めは使うたびに normalizeDucking が行う
 * (保存経路 = UI / AI の正規化では必ず正規化済みの値を書く)。
 */
export const DuckingSchema = z.object({
  enabled: z.boolean(),
  // **4 つとも素の number** (範囲も符号も縛らない): 負値の JSON を PUT されても parse が throw
  // せず、`normalizeDucking` が範囲へクランプして実効化する (1 つの壊れた値でプロジェクトが
  // 開けなくなる / MP4 が落ちるのを避ける)。保存時の検証は backend が担う。
  targetDb: z.number().optional(),
  attackSec: z.number().optional(),
  releaseSec: z.number().optional(),
  holdSec: z.number().optional(),
});
export type Ducking = z.infer<typeof DuckingSchema>;

/**
 * ラウドネス正規化の設定 (プロジェクト単位。§4.3-M)。
 * 意味・既定値・クランプの正本は audio/loudness.ts (3 実装で共有する純関数)。
 *
 * DuckingSchema と同じく**範囲を厳しく縛らない** (壊れた JSON でプロジェクト全体が開けなく
 * なるのを避け、クランプと丸めは使うたびに normalizeLoudness が行う)。
 * **掛かるのは MP4 書き出し (Cloud Run レンダラー) の後処理だけ**で、プレビューには効かない。
 */
export const LoudnessSchema = z.object({
  enabled: z.boolean(),
  targetLufs: z.number().optional(),
  truePeakDb: z.number().optional(),
  lra: z.number().optional(),
});
export type Loudness = z.infer<typeof LoudnessSchema>;

export const LookPresetSchema = z.enum(LOOK_PRESETS as [LookPreset, ...LookPreset[]]);
export const LookSchema = z.object({
  preset: LookPresetSchema,
  intensity: z.number().min(0).max(1).optional(),
  enabled: z.boolean().optional(),
});
export type Look = z.infer<typeof LookSchema>;

/**
 * プロジェクト本体のスキーマ (旧 `bgm` の移行を挟まない素の形)。
 * 通常は移行を通した `EditProjectSchema` を使うこと。
 */
export const EditProjectObjectSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  createdAt: z.string(),
  updatedAt: z.string(),
  prompt: z.string(),
  sourceClips: z.array(SourceClipSchema),
  timeline: z.array(TimelineItemSchema),
  // BGM トラック。旧 `bgm`(単一)は読み込み時に 1 キューの配列へ移行する (§5.3 (a))
  // ので、正規化後は常に配列。[] = BGM なし。
  bgmTrack: z.array(AudioCueSchema).optional(),
  // SE (効果音) トラック。キューの型は BGM と共通で、違うのは検証規則 (最小尺 0.05 秒)・
  // 既定音量 (1.0)・`loop: false` を明示することの 3 点だけ (§4.3-D)。
  seTrack: z.array(AudioCueSchema).optional(),
  // AI が曲替えをするための候補曲 (§9.4)。描画・書き出しには使わない。
  bgmCandidates: z.array(BgmCandidateSchema).optional(),
  // BGM のビート検出結果 (§4.3-T)。materialId → {tempoBpm, beats, confidence, ...}。
  // 解析ジョブが書く導出値で、描画・書き出しには使わない (拍への吸着にだけ読む)。
  // 署名付き URL ではないので stripResolvedUrls の対象外 = そのまま保存する。
  bgmAnalysis: BgmAnalysisMapSchema,
  // AI / UI が効果音を置くための候補 SE (§4.3-D)。描画・書き出しには使わない。
  seCandidates: z.array(SeCandidateSchema).optional(),
  // ナレーショントラック。undefined = 旧データ(クリップ内でのナレーション再生に
  // フォールバック)。[] = 明示的にナレーションなし。
  narrationTrack: z.array(NarrationCueSchema).optional(),
  output: OutputSettingsSchema,
  // トラック単位の「動画に含めない」フラグ (エディタの行ラベル クリックで一括切替)。
  // true のトラックはレンダリング (プレビュー・書き出し) から外す。省略 = すべて含める。
  trackMutes: TrackMutesSchema.optional(),
  // カラーグレーディング (プリセット)。プロジェクト全体の映像レイヤーに 1 つだけ掛ける
  // (テロップ・トランジション等は対象外)。未設定 (undefined / null) = カラーグレーディングなし
  // (旧プロジェクト JSON はそのまま読める)。enabled: false は「設定は残すが掛けない」
  // (UI のスイッチ OFF)。実効パラメータは lookCatalog.resolveLookParams で解決する。
  look: LookSchema.nullable().optional(),
  // ナレーション中の BGM 自動ダッキング (§4.3-E)。未設定 (undefined / null) = 無効
  // (旧プロジェクト JSON はそのまま読め、音も変わらない)。対象は bgmTrack だけで、
  // 効果音 (seTrack)・シーン音声・ナレーションには掛からない。
  ducking: DuckingSchema.nullable().optional(),
  // ラウドネス正規化 (§4.3-M)。未設定 (undefined / null) = 無効 (旧プロジェクト JSON はそのまま
  // 読め、書き出しの音も変わらない)。**掛かるのは MP4 書き出しの後処理だけ**で、
  // プレビュー (Player) には効かない (UI に「書き出し (MP4) 時に適用」と明記する)。
  loudness: LoudnessSchema.nullable().optional(),
  // オーバーレイ (画像 / ぼかし / モザイクの矩形)。**動画全体の絶対秒**を基準にした区間で、
  // 省略 = 全編 (§4.1-F)。ロゴのウォーターマーク・バッジ用。描画は**シーン層の外に 1 枚**
  // (motion / look の外側で、テロップ・トランジションの白レイヤーより**上**)。
  // 未設定 = オーバーレイなし。
  overlays: OverlayArraySchema.optional(),
  // 組み立て元シナリオのスナップショット (上記 SourceSnapshotSchema)。未設定 = 旧プロジェクト。
  sourceSnapshot: SourceSnapshotSchema.nullable().optional(),
  history: z.array(HistoryEntrySchema).default([]),
  // 形式バージョン (lineage 計画 §8.2)。無し = 0 (lineage / 錨の無い旧形式)、2 = 本計画の形式
  // (1 は飛ばして「無し」と区別しやすくする)。読み込み時の移行 (validateProject) で 2 にする。
  // BE の保存は、保存済みの JSON より小さい formatVersion の本文を 409 (format_outdated) で拒否する
  // (旧タブの FE が未知キーを落として保存するのを防ぐ)。
  formatVersion: z.number().int().nonnegative().optional(),
  // 素材起点 (origin='material') の取り込み (プレゼン動画取り込み計画 §3.2)。どちらも BE が書く導出値で、
  // 描画・書き出しには使わない。未設定 = シナリオ起点、または取り込み未完了。
  //   importAnalyzed: 取り込みジョブが analysis.script を保存した世代 (FE はこれの有無で「取り込み済み」を判定する)
  //   importBaseline: 作成時に create_project が書く初期 project の正規化ハッシュ (再解析時の手動編集判定の基準)
  importAnalyzed: z
    .object({ importVersion: z.number().int().nonnegative(), jobId: z.string(), savedAt: z.string() })
    .optional(),
  importBaseline: z.object({ snapshot: z.string() }).optional(),
});

/** 本計画 (lineage / 錨) の形式バージョン。validateProject が読み込み時にこの値へ移行する。 */
export const EDIT_PROJECT_FORMAT_VERSION = 2;

/**
 * プロジェクトのスキーマ。**入口で旧 `bgm` を `bgmTrack` へ移行**してから検証する (§5.3 (a))。
 * validateProject だけでなく Remotion の inputProps 検証 (EditVideoPropsSchema) など、
 * このスキーマを直接 parse する経路もすべて旧 JSON を受けられるようにするため、
 * z.preprocess で包んでいる。
 */
export const EditProjectSchema = z.preprocess(migrateProjectBgmTrack, EditProjectObjectSchema);
export type EditProject = z.infer<typeof EditProjectSchema>;

/**
 * 話者ごとの既定の文字色。design 未指定のテロップは話者ごとに色を塗り分ける
 * (ナレーションと話者不明は白)。縦型は派手な色、横型は白を基準にした柔らかい色違い。
 * 割り当ては「プロジェクト内での話者の初出順」(buildCaptionSpeakerColorMap) で決める。
 * video-editor リポジトリの src/schema/project.ts にも同じ定義がある。
 */
export const CAPTION_SPEAKER_COLORS_PORTRAIT = [
  '#FFE600', // 黄
  '#4DF0FF', // シアン
  '#FF6EB4', // ピンク
  '#B6FF4A', // ライム
  '#FFA640', // オレンジ
  '#C79BFF', // ラベンダー
];
export const CAPTION_SPEAKER_COLORS_LANDSCAPE = [
  '#FFF2B3', // クリーム
  '#CDEBFF', // 淡い水色
  '#FFD9E6', // 淡いピンク
  '#DDFFD1', // 淡いミント
  '#FFE0C2', // 淡いオレンジ
  '#E6DAFF', // 淡いラベンダー
];

/**
 * プロジェクト内に登場する話者の一覧 (初出順・重複なし)。テロップの話者ドロップダウンの選択肢に使う。
 * ナレーションとラベルなしの「その他」は、登場していなくても常に選べるよう末尾に補う。
 */
export function collectProjectSpeakers(project: Pick<EditProject, 'timeline'>): CaptionSpeaker[] {
  const byKey = new Map<string, CaptionSpeaker>();
  for (const item of project.timeline) {
    for (const caption of item.captions) {
      if (!caption.speaker) continue;
      const key = captionSpeakerKey(caption.speaker);
      if (!byKey.has(key)) byKey.set(key, caption.speaker);
    }
  }
  const narration: CaptionSpeaker = { kind: 'narration' };
  const other: CaptionSpeaker = { kind: 'other' };
  if (!byKey.has(captionSpeakerKey(narration))) byKey.set(captionSpeakerKey(narration), narration);
  if (!byKey.has(captionSpeakerKey(other))) byKey.set(captionSpeakerKey(other), other);
  return Array.from(byKey.values());
}

/** 話者キー → 既定の文字色。タイムライン順にテロップを走査し、話者の初出順にパレットを割り当てる。 */
export function buildCaptionSpeakerColorMap(
  project: Pick<EditProject, 'timeline' | 'output'>,
): Map<string, string> {
  const palette =
    isPortraitOutput(project.output)
      ? CAPTION_SPEAKER_COLORS_PORTRAIT
      : CAPTION_SPEAKER_COLORS_LANDSCAPE;
  const map = new Map<string, string>();
  for (const item of project.timeline) {
    for (const caption of item.captions) {
      const speaker = caption.speaker;
      if (!speaker || speaker.kind === 'narration') continue;
      const key = captionSpeakerKey(speaker);
      if (!map.has(key)) map.set(key, palette[map.size % palette.length]!);
    }
  }
  return map;
}

/**
 * テロップ 1 件の既定デザイン (design 未指定のときの表示・編集の初期値)。
 * defaultCaptionDesignFor に、話者ごとの既定の文字色 (speakerColors) を重ねる。
 */
export function defaultCaptionDesignForCaption(
  output: { width: number; height: number },
  caption: Pick<Caption, 'speaker'>,
  speakerColors: Map<string, string>,
): CaptionDesign {
  const base = defaultCaptionDesignFor(output);
  const color = caption.speaker ? speakerColors.get(captionSpeakerKey(caption.speaker)) : undefined;
  return color ? { ...base, color } : base;
}

/**
 * タイムライン整合性エラー。complete な理由文字列を持つ。
 */
export class ProjectValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectValidationError';
  }
}

/**
 * 未加工の data を EditProject として検証・正規化する。
 * - zod によるスキーマ検証(materialId 必須を含む)
 * - タイムライン整合性チェック:
 *   - 各 TimelineItem.clipId が sourceClips に実在すること
 *   - inSec < outSec であること
 *   - outSec が参照している素材の durationSec を超えないこと
 *
 * AI (Claude) の出力・ユーザーの手動編集の両方が、保存前に必ずこの関数を通過する想定。
 */
/**
 * 鳴らせない SE キュー (素材の実尺 `sourceDurationSec` が無い / 素材参照 `materialId` が無い) を落とす (§4.3-D)。
 *
 * SE は `endSec` 省略 = 素材の実尺どおり 1 回鳴らす設計なので、実尺が無いと区間の終わりが
 * 決まらず「動画の残り全部」鳴ってしまう (タイムラインのマーカーも書き出しのクリップ長も全幅)。
 * 保存時には BE の `validate_edit_project_json` が 400 で弾くが、それ以前に保存された JSON や
 * 外部から直接書き換えられた JSON が読み込めなくなると**プロジェクトごと開けなくなる**ため、
 * 読み込み側は投げずに「そのキューだけ落として注記」する (§0-2 の書き出しと同じ方針)。
 *
 * 3 実装 (FE / レンダラー / backend) で同じ規則。
 */
export function dropUnusableSeCues<
  C extends { id: string; sourceDurationSec?: number; materialId?: string },
>(cues: C[]): { kept: C[]; dropped: C[] } {
  const kept: C[] = [];
  const dropped: C[] = [];
  for (const cue of cues) {
    const hasDuration = cue.sourceDurationSec != null && cue.sourceDurationSec > 0;
    // materialId が無い cue は「AI の周の中だけのプレースホルダー」(catalogId しか無い) が
    // 何かの拍子に届いたもの。素材を指していないので鳴らせない (§4.3-D)。
    const hasMaterial = typeof cue.materialId === 'string' && cue.materialId.length > 0;
    if (hasDuration && hasMaterial) kept.push(cue);
    else dropped.push(cue);
  }
  if (dropped.length > 0) {
    console.warn(
      `[videoEditor] 素材の尺が分からない効果音 ${dropped.length} 件を読み込み時に除外しました ` +
        `(${dropped.map((c) => c.id).join(', ')})`,
    );
  }
  return { kept, dropped };
}

export function validateProject(data: unknown): EditProject {
  const project = EditProjectSchema.parse(data);

  const clipsById = new Map(project.sourceClips.map((clip) => [clip.id, clip]));

  for (const item of project.timeline) {
    const clip = clipsById.get(item.clipId);
    if (!clip) {
      throw new ProjectValidationError(
        `timeline item "${item.id}" references unknown clipId "${item.clipId}"`,
      );
    }
    if (item.inSec >= item.outSec) {
      throw new ProjectValidationError(
        `timeline item "${item.id}" has inSec (${item.inSec}) >= outSec (${item.outSec})`,
      );
    }
    if (item.outSec > clip.durationSec) {
      throw new ProjectValidationError(
        `timeline item "${item.id}" has outSec (${item.outSec}) exceeding clip "${clip.id}" duration (${clip.durationSec})`,
      );
    }
  }

  // 音声キューの不変条件 (§5.2)。フェードの合計だけは拒否せずクランプする。
  const totalSec = projectDuration(project);
  const bgmTrack = project.bgmTrack?.map((cue, index) =>
    validateAudioCue(cue, `bgmTrack[${index}]`, totalSec),
  );
  // SE は「素材の実尺どおり 1 回鳴らす」が既定なので、実尺が無いと区間の終わりが決まらず
  // 「動画の残り全部」鳴ってしまう。とはいえ 1 本の壊れたキューでプロジェクト全体を開けなく
  // するのは割に合わないので、**そのキューだけ落として注記**する (§0-2 と同じ方針)。
  const seTrack = project.seTrack
    ? dropUnusableSeCues(project.seTrack).kept.map((cue, index) =>
        validateAudioCue(cue, `seTrack[${index}]`, totalSec, 'se'),
      )
    : undefined;

  // 音量 / フェードの範囲外は throw せずクランプして注記する (§4.3-E)。保存時の検証は
  // backend の validate_edit_project_json が担う (ここは読み込み側の最後の防衛線)。
  // オーバーレイ (§4.1-F / §4.1-G) の「使えない 1 件だけ落とす」は OverlayArraySchema の
  // 前処理 (dropUnusableOverlayEntries) が済ませている (スキーマで throw しない)。
  // テロップは常に時系列順 (schema/captionOrder.ts)。古い保存データもここで揃う
  const timeline = sortTimelineCaptions(
    project.timeline.map((item, index) => clampItemAudioRegions(clampItemAudioFades(item, `timeline[${index}]`))),
  );
  const narrationTrack = project.narrationTrack?.map((cue, index) =>
    clampNarrationCueAudio(cue, `narrationTrack[${index}]`),
  );

  for (const cue of project.narrationTrack ?? []) {
    const clip = clipsById.get(cue.clipId);
    if (!clip) {
      throw new ProjectValidationError(
        `narration cue "${cue.id}" references unknown clipId "${cue.clipId}"`,
      );
    }
    // 音声参照は materialId ベース (narrationSrc は導出値のため保存データには無い)
    if (!clip.narrationMaterialId && !clip.narrationSrc) {
      throw new ProjectValidationError(
        `narration cue "${cue.id}" references clip "${cue.clipId}" which has no narration audio`,
      );
    }
  }

  // 元の project に無かったキーを `undefined` の値として生やさない (JSON 化で消えるとはいえ、
  // `'narrationTrack' in project` のようなキーの有無で三値を判定する箇所を濁らせる)。
  const normalized: EditProject = {
    ...project,
    timeline,
    ...(project.narrationTrack !== undefined ? { narrationTrack } : {}),
    ...(project.bgmTrack !== undefined ? { bgmTrack } : {}),
    ...(project.seTrack !== undefined ? { seTrack } : {}),
  };
  return migrateProjectFormat(normalized);
}

/**
 * 出自 (lineage) と錨 (anchor) の形式への移行と、錨からの秒の引き直し (lineage 計画 §3.5 / §4.7 / §5.5 / §8.2)。
 * validateProject の最後に必ず通る (読み込み・組み立て・保存前の正規化のすべて)。
 * - lineage の無い項目を推定で埋める (推定には inferred)
 * - 音声クリップの audioSource を snapshot から埋める
 * - formatVersion < 2 なら全 cue に錨を補う。2 ならナレーション / SE / BGM に錨が無いときだけ保険で補い、記録する
 * - 錨から startSec / endSec / silenced を引き直し、formatVersion を 2 にする
 */
function migrateProjectFormat(project: EditProject): EditProject {
  const formatVersion = project.formatVersion ?? 0;
  const clipsById = new Map(project.sourceClips.map((clip) => [clip.id, clip]));
  const timeline = inferTimelineLineage(project.timeline, clipsById, storyboardBodyClipIds(project));
  let next: EditProject = timeline === project.timeline ? project : { ...project, timeline };
  next = inferAudioSources(next);
  const migrated = migrateLegacyAnchors(next, { includeOverlays: formatVersion < EDIT_PROJECT_FORMAT_VERSION });
  if (formatVersion >= EDIT_PROJECT_FORMAT_VERSION && migrated.filled > 0) {
    console.warn(`[videoEditor] formatVersion ${formatVersion} のプロジェクトで錨の無い cue ${migrated.filled} 件を配置から補いました`);
  }
  next = recomputeAnchoredTiming(migrated.project);
  if (next.formatVersion !== EDIT_PROJECT_FORMAT_VERSION) next = { ...next, formatVersion: EDIT_PROJECT_FORMAT_VERSION };
  return next;
}

/**
 * ストーリーボードモードの本編クリップの id (lineage の推定 §3.5 で「区間項目 = sceneId を付けない」対象)。
 * 組み立て (buildStoryboardModeTimeline) は本編クリップに「ストーリーボード本編」のラベルを付け、区間ごとに
 * 複数の項目から参照させる。ブランドカット / 予告編の静止画は通常モードと同じ 1 クリップ = 1 項目。
 */
function storyboardBodyClipIds(project: EditProject): Set<string> {
  const ids = new Set<string>();
  if (project.sourceSnapshot?.frameMode !== 'storyboard') return ids;
  const itemCount = new Map<string, number>();
  for (const item of project.timeline) itemCount.set(item.clipId, (itemCount.get(item.clipId) ?? 0) + 1);
  for (const clip of project.sourceClips) {
    if (clip.type !== 'video' || !clip.sourceSceneId) continue;
    if ((clip.label ?? '').startsWith('ストーリーボード本編') || (itemCount.get(clip.id) ?? 0) >= 2) ids.add(clip.id);
  }
  return ids;
}

/**
 * 音声キャリアクリップ (sourceSceneId 無し・narrationMaterialId あり・タイムラインから参照されない) の
 * audioSource を旧データから推定する (lineage 計画 §5.5)。audioSource を持つクリップは触らない。
 * - snapshot に同じ素材 ID のシーンがちょうど 1 つ → 'scenario'・そのシーンを sourceSceneId に
 * - 複数 → 錨の先の項目の lineage (sceneId / materialSceneId) と一致するシーンがあればそれ。無ければ 'unknown'
 * - 無い / snapshot 自体が無い → 'unknown'
 */
function inferAudioSources(project: EditProject): EditProject {
  const referenced = new Set(project.timeline.map((item) => item.clipId));
  const targets = project.sourceClips.filter(
    (clip) => !clip.audioSource && !clip.sourceSceneId && clip.narrationMaterialId && !referenced.has(clip.id),
  );
  if (targets.length === 0) return project;
  const scenesByMaterial = new Map<string, string[]>();
  for (const scene of project.sourceSnapshot?.scenes ?? []) {
    if (!scene.narrationMaterialId) continue;
    const list = scenesByMaterial.get(scene.narrationMaterialId) ?? [];
    list.push(scene.sceneId);
    scenesByMaterial.set(scene.narrationMaterialId, list);
  }
  const itemsById = new Map(project.timeline.map((item) => [item.id, item]));
  const anchoredSceneIds = (clipId: string): Set<string> => {
    const ids = new Set<string>();
    for (const cue of project.narrationTrack ?? []) {
      if (cue.clipId !== clipId || cue.anchor?.kind !== 'item') continue;
      const lineage = itemsById.get(cue.anchor.itemId)?.lineage;
      const sceneId = lineage?.sceneId ?? lineage?.materialSceneId;
      if (sceneId) ids.add(sceneId);
    }
    return ids;
  };
  const targetIds = new Set(targets.map((clip) => clip.id));
  const sourceClips = project.sourceClips.map((clip) => {
    if (!targetIds.has(clip.id)) return clip;
    const candidates = scenesByMaterial.get(clip.narrationMaterialId!) ?? [];
    let sceneId: string | undefined;
    if (candidates.length === 1) sceneId = candidates[0];
    else if (candidates.length > 1) {
      const anchored = anchoredSceneIds(clip.id);
      sceneId = candidates.find((id) => anchored.has(id));
    }
    if (sceneId) return { ...clip, audioSource: 'scenario' as const, audioSourceInferred: true, sourceSceneId: sceneId };
    return { ...clip, audioSource: 'unknown' as const, audioSourceInferred: true };
  });
  return { ...project, sourceClips };
}

/**
 * 音声キュー 1 本の不変条件 (§5.2)。
 * - `endSec != null` なら `endSec > startSec`、かつ区間長は最小区間以上
 *   (BGM 0.5 秒 / SE 0.05 秒。`kind` で切り替える。§4.3-D。**`endSec` 省略のキューは検査しない**ので、
 *   SE の既定 (endSec なし = 素材の実尺どおり 1 回) は 0.2 秒の効果音でも通る)
 * - SE の `sourceDurationSec` 欠落は**ここでは投げない**。`dropUnusableSeCues` が
 *   validateProject の手前でそのキューだけ落とす (§0-2 の「落として注記」。1 本の壊れたキューで
 *   プロジェクト全体が開けなくなるのを避ける)
 * - `sourceStartSec < sourceDurationSec` (素材尺が分かるとき。以降は無音になるため)
 * - `fadeInSec + fadeOutSec <= 区間長` は**拒否せずクランプ**する
 *   (AI もユーザーも区間を縮めた拍子に超えるので、音の方を合わせる)
 *
 * 「合計尺の外へ出るキューを落とす」は保存前の正規化 (BE) の仕事なのでここではしない。
 */
function validateAudioCue(
  cue: AudioCue,
  label: string,
  totalSec: number,
  kind: AudioTrackKind = 'bgm',
): AudioCue {
  const minSec = minAudioCueSec(kind);
  if (cue.endSec != null) {
    if (cue.endSec <= cue.startSec) {
      throw new ProjectValidationError(
        `${label} has endSec (${cue.endSec}) <= startSec (${cue.startSec})`,
      );
    }
    if (cue.endSec - cue.startSec < minSec) {
      throw new ProjectValidationError(
        `${label} is shorter than ${minSec}s (startSec ${cue.startSec}, endSec ${cue.endSec})`,
      );
    }
  }

  if (cue.sourceDurationSec != null && (cue.sourceStartSec ?? 0) >= cue.sourceDurationSec) {
    throw new ProjectValidationError(
      `${label} has sourceStartSec (${cue.sourceStartSec}) >= sourceDurationSec (${cue.sourceDurationSec})`,
    );
  }

  // 区間長: endSec が無いキューは「動画の終わりまで」なので合計尺から求める
  const spanSec = Math.max(0, effectiveEndSec(cue, totalSec) - cue.startSec);
  const fadeInSec = cue.fadeInSec ?? 0;
  const fadeOutSec = cue.fadeOutSec ?? 0;
  if (spanSec > 0 && fadeInSec + fadeOutSec > spanSec) {
    const scale = spanSec / (fadeInSec + fadeOutSec);
    return { ...cue, fadeInSec: fadeInSec * scale, fadeOutSec: fadeOutSec * scale };
  }
  return cue;
}

/**
 * excluded=false のタイムラインアイテムのみを対象に、プロジェクト全体の尺(秒)を計算する。
 *
 * cut 以外のトランジション (crossfade / wipe / slide / zoom / whiteout / flash) は前後の
 * クリップを重ねて再生するため、その分だけ合計尺から差し引く (重なりの式は種類によらず
 * 同じ = schema/transition.ts の transitionOverlapSec)。トランジションは「このアイテムの
 * 先頭に適用」される仕様のため、直前に採用済みアイテムが存在する場合にのみ計上する。
 */
export function projectDuration(project: EditProject): number {
  const includedItems = project.timeline.filter((item) => !item.excluded);

  let total = 0;
  let hasPrevious = false;

  for (const item of includedItems) {
    const itemDuration = itemOnScreenDurationSec(item);
    total += itemDuration;

    if (hasPrevious) {
      const overlap = Math.min(transitionOverlapSec(item.transition), itemDuration);
      total -= overlap;
    }

    hasPrevious = true;
  }

  return Math.max(0, total);
}
